// Optional FAQs and internal blog links for glossary entries.
// Keyed by glossary slug — merged at render time in GlossarEntry.tsx.

export type GlossaryFAQ = { q: string; a: string };
export type GlossaryBlogLink = { path: string; label: string };

export type GlossaryExtras = {
  faqs?: GlossaryFAQ[];
  blogLinks?: GlossaryBlogLink[];
};

export const glossaryExtras: Record<string, GlossaryExtras> = {
  // ===== NEW TERMS =====

  altersvorsorgedepot: {
    blogLinks: [
      { path: "/blog/altersvorsorgedepot-2027-riester-nachfolger", label: "Altersvorsorgedepot – der Riester-Nachfolger" },
    ],
    faqs: [
      { q: "Was ist das Altersvorsorgedepot?", a: "Das Altersvorsorgedepot ist ein ab 2027 verfügbares staatlich gefördertes Wertpapierdepot. Erstmals kannst Du damit ETFs und Fonds direkt mit staatlichen Zulagen besparen." },
      { q: "Ab wann gibt es das Altersvorsorgedepot?", a: "Geplant ist der Start zum 1. Januar 2027 als Nachfolger der Riester-Rente." },
      { q: "Was ist der Unterschied zur Riester-Rente?", a: "Anders als Riester gibt es keine Beitragsgarantie – dafür echte Renditechancen über ETFs. Auch Selbstständige sind erstmals förderberechtigt." },
      { q: "Wer ist förderberechtigt?", a: "Alle Arbeitnehmer, Beamte und – neu – auch Selbstständige können das Altersvorsorgedepot nutzen." },
      { q: "Was passiert mit meinem bestehenden Riester-Vertrag?", a: "Bestandsverträge laufen weiter. Du kannst Dich entscheiden, sie zu behalten oder ins neue Altersvorsorgedepot zu wechseln." },
    ],
  },

  "arbeitnehmer-sparzulage": {
    blogLinks: [
      { path: "/blog/staatliche-foerderungen-altersvorsorge-2026", label: "Staatliche Förderungen für Deine Altersvorsorge" },
    ],
    faqs: [
      { q: "Was ist die Arbeitnehmer-Sparzulage?", a: "Eine staatliche Förderung auf vermögenswirksame Leistungen (VL) nach § 13 5. VermBG. Höhe und Deckel hängen von der Anlageform ab: 20 % auf bis zu 400 €/Jahr bei Wertpapier-VL (max. 80 € Zulage), 9 % auf bis zu 470 €/Jahr bei Bauspar-VL (max. 42,30 € Zulage)." },
      { q: "Wer bekommt die Arbeitnehmer-Sparzulage?", a: "Arbeitnehmer, deren zu versteuerndes Einkommen im Anlagejahr 40.000 € (Ledige) bzw. 80.000 € (Zusammenveranlagte) nicht übersteigt. Die Grenze gilt seit 2024 einheitlich für Wertpapier- und Bauspar-VL." },
      { q: "Wie beantrage ich die Arbeitnehmer-Sparzulage?", a: "Du beantragst sie über die Anlage VL in Deiner Einkommensteuererklärung. Die Auszahlung erfolgt am Ende der siebenjährigen Sperrfrist Deines VL-Vertrags." },
      { q: "Wie hoch ist die Einkommensgrenze?", a: "40.000 € (Ledige) bzw. 80.000 € (Zusammenveranlagte) zu versteuerndes Einkommen. Seit 2024 gilt diese Grenze einheitlich für beide Anlageformen — vorher gab es getrennte, niedrigere Grenzen für Bauspar-VL." },
    ],
  },

  ausschuettend: {
    blogLinks: [
      { path: "/blog/etf-vs-tagesgeld-2024", label: "ETF vs. Tagesgeld – was lohnt sich mehr?" },
    ],
    faqs: [
      { q: "Was bedeutet ausschüttend bei ETFs?", a: "Ausschüttende ETFs zahlen Dividenden und Zinsen regelmäßig auf Dein Verrechnungskonto aus – meist quartalsweise oder jährlich." },
      { q: "Ausschüttend oder thesaurierend – was ist besser?", a: "Für den langfristigen Vermögensaufbau sind thesaurierende ETFs steuerlich oft einfacher und der Zinseszins wirkt automatisch. Wer ein passives Einkommen möchte, wählt ausschüttend." },
      { q: "Wie werden Ausschüttungen besteuert?", a: "Ausschüttungen unterliegen der Abgeltungssteuer (25 % + Soli + ggf. Kirchensteuer). Bis zum Sparerpauschbetrag bleiben sie mit Freistellungsauftrag steuerfrei." },
    ],
  },

  depot: {
    blogLinks: [
      { path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" },
      { path: "/blog/etf-portfolio-aufbauen-anleitung", label: "ETF-Portfolio aufbauen – Schritt-für-Schritt-Anleitung" },
      { path: "/blog/depot-check-portfolio-kurs-2026", label: "Depot-Check: Ist Dein Portfolio noch auf Kurs?" },
    ],
    faqs: [
      { q: "Was ist ein Depot?", a: "Ein Depot ist ein Konto bei einer Bank oder einem Broker, auf dem Deine Wertpapiere wie ETFs, Aktien und Fonds verwahrt werden." },
      { q: "Welches Depot ist für Einsteiger geeignet?", a: "Ein günstiges Online-Depot mit kostenfreien ETF-Sparplänen ist ideal. Wichtige Kriterien: Kosten, Sparplanangebot und Benutzerfreundlichkeit." },
      { q: "Was kostet ein Depot?", a: "Viele Online-Broker bieten Depots heute kostenlos an. Es fallen lediglich Transaktionskosten beim Kauf und Verkauf an." },
      { q: "Wie eröffne ich ein Depot?", a: "Online in 10–15 Minuten: Antrag ausfüllen, per Video legitimieren, Verrechnungskonto wird automatisch mit eröffnet." },
    ],
  },

  gesamtkostenquote: {
    blogLinks: [
      { path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" },
    ],
    faqs: [
      { q: "Was ist die TER?", a: "Die Total Expense Ratio (TER) ist die jährliche Gesamtkostenquote eines Fonds oder ETFs in Prozent des angelegten Kapitals." },
      { q: "Wie beeinflusst die TER die Rendite?", a: "Jeder Prozentpunkt TER reduziert Deine Rendite direkt – über 30 Jahre kann das mehrere Zehntausend Euro ausmachen." },
      { q: "Was ist eine gute TER?", a: "Günstige Welt-ETFs liegen bei 0,1–0,2 % p. a. Aktiv gemanagte Fonds liegen oft bei 1,5–2,5 % – ein riesiger Renditenachteil." },
    ],
  },

  grundrente: {
    blogLinks: [
      { path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" },
    ],
    faqs: [
      { q: "Was ist die Grundrente?", a: "Eine staatliche Aufstockung der gesetzlichen Rente für Menschen, die lange gearbeitet, aber wenig verdient haben." },
      { q: "Wer bekommt die Grundrente?", a: "Wer mindestens 33 Jahre Beitragszeiten vorweisen kann und unterdurchschnittlich verdient hat." },
      { q: "Wie hoch ist die Grundrente?", a: "Im Schnitt liegt der Zuschlag bei rund 75–80 € pro Monat, in einigen Fällen auch über 400 €." },
      { q: "Muss ich die Grundrente beantragen?", a: "Nein. Seit 2021 prüft und zahlt die Deutsche Rentenversicherung den Zuschlag automatisch aus." },
    ],
  },

  kaufkraft: {
    blogLinks: [
      { path: "/blog/inflation-erspartes-schuetzen-2024", label: "Inflation – wie Du Dein Erspartes schützt" },
    ],
    faqs: [
      { q: "Was ist Kaufkraft?", a: "Die Kaufkraft beschreibt, wie viele Güter und Dienstleistungen Du für einen bestimmten Geldbetrag tatsächlich bekommst." },
      { q: "Wie erhalte ich meine Kaufkraft?", a: "Indem Du Dein Geld so anlegst, dass die Rendite über der Inflationsrate liegt – z. B. mit breit gestreuten ETFs oder Sachwerten." },
      { q: "Was frisst die Kaufkraft auf?", a: "Vor allem Inflation in Verbindung mit niedrig oder gar nicht verzinstem Geld auf Giro- und Sparkonten." },
    ],
  },

  krankentagegeld: {
    blogLinks: [
      { path: "/leistungen", label: "Unsere Leistungen im Überblick" },
      { path: "/blog/krankentagegeld-luecke-absicherung", label: "Krankentagegeld: Wann die Lücke gefährlich wird" },
      { path: "/blog/arbeitskraft-absichern-versicherungen", label: "Arbeitskraft absichern – welche Versicherungen wirklich zählen" },
    ],
    faqs: [
      { q: "Was ist Krankentagegeld?", a: "Eine Versicherungsleistung, die Dein Einkommen absichert, wenn Du länger als 6 Wochen krank bist und der Arbeitgeber die Lohnfortzahlung einstellt." },
      { q: "Wer braucht Krankentagegeld?", a: "Vor allem Selbstständige, Freiberufler und Gutverdiener, deren Krankengeld der gesetzlichen Kasse nicht zum Lebensstandard ausreicht." },
      { q: "Wie hoch sollte das Krankentagegeld sein?", a: "Es sollte die Lücke zwischen gesetzlichem Krankengeld und Deinem Netto-Einkommen schließen – idealerweise rund 80–100 % Deines Nettos." },
    ],
  },

  nettolohn: {
    blogLinks: [
      { path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" },
    ],
    faqs: [
      { q: "Was ist der Unterschied zwischen Brutto und Netto?", a: "Brutto ist Dein vereinbartes Gehalt, Netto das, was nach Steuern und Sozialabgaben tatsächlich auf Deinem Konto landet." },
      { q: "Wie berechne ich meinen Nettolohn?", a: "Am einfachsten mit einem Brutto-Netto-Rechner – berücksichtigt werden Steuerklasse, Kirchensteuer, Krankenkasse und Freibeträge." },
      { q: "Wie kann ich mehr Netto vom Brutto bekommen?", a: "Über Entgeltumwandlung, vermögenswirksame Leistungen, Sachbezüge und steuerfreie Arbeitgeberzuschüsse." },
    ],
  },

  progressionsvorbehalt: {
    blogLinks: [
      { path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" },
      { path: "/blog/steuererklarung-2025-fehler-vermeiden", label: "Steuererklärung – häufige Fehler vermeiden" },
    ],
    faqs: [
      { q: "Was ist der Progressionsvorbehalt?", a: "Er erhöht Deinen persönlichen Steuersatz auf das steuerpflichtige Einkommen, auch wenn bestimmte Einnahmen selbst steuerfrei sind." },
      { q: "Welche Einnahmen unterliegen dem Progressionsvorbehalt?", a: "Z. B. Kurzarbeitergeld, Elterngeld, Arbeitslosengeld I, Krankengeld oder Insolvenzgeld." },
      { q: "Muss ich dann eine Steuererklärung machen?", a: "Ja. Wer im Jahr mehr als 410 € an Lohnersatzleistungen erhalten hat, ist zur Abgabe einer Steuererklärung verpflichtet." },
    ],
  },

  rebalancing: {
    blogLinks: [
      { path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" },
    ],
    faqs: [
      { q: "Was ist Rebalancing?", a: "Rebalancing bedeutet, die ursprüngliche Gewichtung Deines Portfolios regelmäßig wiederherzustellen, damit Dein Risikoprofil erhalten bleibt." },
      { q: "Wie oft sollte ich rebalancen?", a: "In der Regel reicht einmal pro Jahr oder bei Abweichungen von mehr als 5–10 % von der Ziel-Allokation." },
      { q: "Ist Rebalancing steuerlich relevant?", a: "Ja. Verkäufe lösen Abgeltungssteuer aus. Smarter ist es deshalb, über neue Sparraten in untergewichtete Positionen umzuschichten." },
    ],
  },

  rentenbescheid: {
    blogLinks: [
      { path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" },
    ],
    faqs: [
      { q: "Was steht im Rentenbescheid?", a: "Die Höhe Deiner Rente, der Berechnungsweg, die anerkannten Beitragszeiten und der genaue Rentenbeginn." },
      { q: "Wie lese ich meinen Rentenbescheid?", a: "Zentrale Werte sind die Entgeltpunkte, der aktuelle Rentenwert und der Zugangsfaktor – das Produkt ergibt Deine monatliche Bruttorente." },
      { q: "Was tun, wenn der Rentenbescheid falsch ist?", a: "Du hast einen Monat Zeit, schriftlich Widerspruch einzulegen – am besten mit Unterstützung einer unabhängigen Beratung." },
    ],
  },

  sonderausgaben: {
    blogLinks: [
      { path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" },
    ],
    faqs: [
      { q: "Was sind Sonderausgaben?", a: "Private Ausgaben, die das Gesetz steuerlich begünstigt – z. B. Vorsorgeaufwendungen, Spenden, Kirchensteuer und Kinderbetreuungskosten." },
      { q: "Was kann ich als Sonderausgaben absetzen?", a: "U. a. Beiträge zu Rürup, Riester, Kranken- und Pflegeversicherung sowie Spenden und Schulgeld." },
      { q: "Wie hoch ist der Sonderausgaben-Pauschbetrag?", a: "Der Pauschbetrag liegt bei 36 € (Single) bzw. 72 € (Verheiratete). Wer höhere Sonderausgaben hat, sollte diese einzeln nachweisen." },
    ],
  },

  thesaurierend: {
    blogLinks: [
      { path: "/blog/etf-vs-tagesgeld-2024", label: "ETF vs. Tagesgeld – was lohnt sich mehr?" },
    ],
    faqs: [
      { q: "Was bedeutet thesaurierend?", a: "Bei thesaurierenden Fonds werden Dividenden und Zinsen nicht ausgezahlt, sondern automatisch im Fonds reinvestiert." },
      { q: "Thesaurierend oder ausschüttend – was ist besser?", a: "Für langfristigen Vermögensaufbau sind thesaurierende ETFs meist effizienter, weil der Zinseszins automatisch wirkt." },
      { q: "Wie wird ein thesaurierender ETF besteuert?", a: "Jährlich über die Vorabpauschale. Diese wird beim späteren Verkauf auf die Steuerlast angerechnet – keine Doppelbesteuerung." },
    ],
  },

  tagesgeld: {
    blogLinks: [
      { path: "/blog/etf-vs-tagesgeld-2024", label: "ETF vs. Tagesgeld – was lohnt sich mehr?" },
    ],
    faqs: [
      { q: "Was ist Tagesgeld?", a: "Eine flexible, täglich verfügbare Geldanlage bei einer Bank mit variablem Zinssatz und gesetzlicher Einlagensicherung bis 100.000 €." },
      { q: "Wie viel Zinsen gibt es auf Tagesgeld?", a: "Aktuell zwischen ca. 1,5 und 3,5 % – abhängig von Bank und Marktphase. Lockangebote für Neukunden sind oft zeitlich begrenzt." },
      { q: "Tagesgeld oder ETF?", a: "Beides. Tagesgeld eignet sich für Deine Notreserve (3–6 Nettogehälter), für langfristigen Vermögensaufbau ist ein ETF überlegen." },
    ],
  },

  werbungskosten: {
    blogLinks: [
      { path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" },
    ],
    faqs: [
      { q: "Was sind Werbungskosten?", a: "Beruflich bedingte Ausgaben, die Du in der Steuererklärung absetzen kannst – z. B. Fahrtkosten, Arbeitsmittel oder Fortbildungen." },
      { q: "Was kann ich als Werbungskosten absetzen?", a: "Pendlerpauschale, Arbeitsmittel, Homeoffice-Pauschale, Fachliteratur, Fortbildungen, Bewerbungskosten u. v. m." },
      { q: "Lohnt es sich, Werbungskosten einzeln nachzuweisen?", a: "Ja, sobald Deine tatsächlichen Werbungskosten den Arbeitnehmer-Pauschbetrag von 1.230 € (2026) übersteigen." },
    ],
  },

  wohnungsbaupraemie: {
    blogLinks: [
      { path: "/blog/staatliche-foerderungen-altersvorsorge-2026", label: "Staatliche Förderungen für Deine Altersvorsorge" },
    ],
    faqs: [
      { q: "Was ist die Wohnungsbauprämie?", a: "Eine staatliche Förderung von 10 % auf Bauspareinlagen für Personen mit niedrigem bis mittlerem Einkommen." },
      { q: "Wer bekommt die Wohnungsbauprämie?", a: "Alle ab 16 Jahren mit einem zu versteuernden Einkommen bis 35.000 € (Single) bzw. 70.000 € (Verheiratete)." },
      { q: "Wie beantrage ich die Wohnungsbauprämie?", a: "Über das offizielle Antragsformular Deiner Bausparkasse – dort wird sie nach Auszahlung automatisch gutgeschrieben." },
    ],
  },

  // ===== EXISTING ENTRIES — enhanced with FAQs + blog links =====

  diversifikation: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was bedeutet Diversifikation?", a: "Die Streuung Deines Kapitals über verschiedene Anlageklassen, Länder und Branchen, um Risiken zu reduzieren." },
      { q: "Warum ist Diversifikation wichtig?", a: "Sie schützt vor Klumpenrisiken – ein Verlust in einem Bereich wird durch Gewinne in anderen ausgeglichen." },
      { q: "Wie diversifiziere ich mein Portfolio?", a: "Am einfachsten über einen breit gestreuten Welt-ETF wie MSCI World oder MSCI ACWI – ergänzt um Anleihen oder Schwellenländer." },
    ],
  },

  entgeltumwandlung: {
    blogLinks: [{ path: "/blog/betriebliche-altersvorsorge-steuerfrei", label: "Betriebliche Altersvorsorge – steuerfrei sparen" }],
    faqs: [
      { q: "Was ist Entgeltumwandlung?", a: "Die Umwandlung von Teilen Deines Bruttogehalts in Beiträge zur betrieblichen Altersvorsorge – steuer- und sozialabgabenfrei." },
      { q: "Wie viel kann ich umwandeln?", a: "2026 sind bis zu 8 % der Beitragsbemessungsgrenze der allgemeinen Rentenversicherung (bundesweit einheitlich 8.450 € pro Monat) steuerfrei – das sind 676 € pro Monat nach § 3 Nr. 63 EStG. Sozialabgabenfrei sind davon die ersten 4 %, also 338 € pro Monat (§ 1 SvEV). Die 4-%-Grenze liegt innerhalb der 8-%-Grenze, nicht zusätzlich zu ihr." },
      { q: "Lohnt sich Entgeltumwandlung?", a: "Ja, vor allem mit Arbeitgeberzuschuss und bei einem niedrigeren Steuersatz im Alter." },
      { q: "Was passiert bei einem Arbeitgeberwechsel?", a: "Du kannst den Vertrag mitnehmen, beitragsfrei stellen oder zum neuen Arbeitgeber übertragen." },
    ],
  },

  etf: {
    blogLinks: [
      { path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" },
      { path: "/blog/etf-vs-tagesgeld-2024", label: "ETF vs. Tagesgeld" },
    ],
    faqs: [
      { q: "Was ist ein ETF?", a: "Ein Exchange Traded Fund ist ein börsengehandelter Indexfonds, der einen Marktindex wie den MSCI World automatisch abbildet." },
      { q: "Wie funktioniert ein ETF?", a: "Er kauft alle Aktien eines Index in der gleichen Gewichtung – ohne aktives Fondsmanagement." },
      { q: "Welcher ETF ist für Einsteiger geeignet?", a: "Ein breit gestreuter Welt-ETF wie MSCI World oder MSCI ACWI ist ein bewährter Einstieg." },
      { q: "Sind ETFs sicher?", a: "ETFs sind Sondervermögen und damit insolvenzgeschützt. Kursschwankungen gehören aber dazu – Anlagehorizont mindestens 10 Jahre." },
      { q: "ETF oder Tagesgeld – was ist besser?", a: "Tagesgeld für die Notreserve, ETFs für langfristigen Vermögensaufbau. Beides ergänzt sich." },
    ],
  },

  freistellungsauftrag: {
    blogLinks: [{ path: "/blog/freistellungsauftrag-richtig-nutzen", label: "Freistellungsauftrag richtig nutzen" }],
    faqs: [
      { q: "Was ist ein Freistellungsauftrag?", a: "Ein Auftrag an Deine Bank, Kapitalerträge bis zum Sparerpauschbetrag steuerfrei auszuzahlen." },
      { q: "Wie viel kann ich freistellen?", a: "1.000 € pro Person bzw. 2.000 € bei zusammenveranlagten Ehepaaren (Stand 2026)." },
      { q: "Was passiert ohne Freistellungsauftrag?", a: "Die Bank zieht automatisch 25 % Abgeltungssteuer ab. Du kannst sie über die Steuererklärung zurückholen." },
      { q: "Kann ich ihn auf mehrere Banken aufteilen?", a: "Ja, die Summe aller Aufträge darf den Pauschbetrag jedoch nicht überschreiten." },
    ],
  },

  inflation: {
    blogLinks: [{ path: "/blog/inflation-erspartes-schuetzen-2024", label: "Inflation – wie Du Dein Erspartes schützt" }],
    faqs: [
      { q: "Was ist Inflation?", a: "Der allgemeine Anstieg des Preisniveaus über die Zeit. Bei 3 % Inflation halbiert sich die Kaufkraft Deines Geldes in rund 24 Jahren." },
      { q: "Wie schütze ich mein Geld vor Inflation?", a: "Mit Sachwerten wie Aktien, ETFs, Immobilien oder Gold, deren Wert langfristig mit oder über der Inflation steigt." },
      { q: "Was ist eine gute Rendite gegen Inflation?", a: "Realrendite zählt: Deine Rendite muss über der Inflationsrate liegen. Breit gestreute ETFs schaffen historisch 5–7 % real." },
    ],
  },

  "msci-world": {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist der MSCI World?", a: "Ein weltweiter Aktienindex mit über 1.500 Unternehmen aus 23 Industrieländern – Standardreferenz für globale ETFs." },
      { q: "Wie investiere ich in den MSCI World?", a: "Am einfachsten über einen ETF, der den Index 1:1 abbildet – per Einmalanlage oder monatlichem Sparplan." },
      { q: "Ist der MSCI World sicher?", a: "Aufgrund der breiten Streuung gilt er als solide langfristige Anlage. Kursschwankungen gibt es trotzdem – mindestens 10 Jahre Horizont sind empfehlenswert." },
    ],
  },

  "private-krankenversicherung": {
    blogLinks: [{ path: "/blog/private-krankenversicherung-wechsel", label: "Private Krankenversicherung – Wechsel sinnvoll?" }],
    faqs: [
      { q: "Was ist die PKV?", a: "Eine Alternative zur gesetzlichen Krankenversicherung – mit individuell wählbaren Leistungen und risikoabhängigen Beiträgen." },
      { q: "Wann lohnt sich die PKV?", a: "Vor allem für Beamte, Selbstständige und Gutverdiener mit stabilem Einkommen und ohne Kinderwunsch in der Mitversicherung." },
      { q: "Was ist der Unterschied zwischen PKV und GKV?", a: "Die GKV ist solidarisch und einkommensabhängig, die PKV kalkuliert nach Alter, Gesundheit und Leistungen." },
      { q: "Kann ich von der PKV zurück in die GKV?", a: "Ein Rückwechsel ist nach dem 55. Lebensjahr in der Regel ausgeschlossen – die Entscheidung will gut überlegt sein." },
    ],
  },

  rentenluecke: {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Was ist die Rentenlücke?", a: "Die Differenz zwischen Deinem letzten Nettogehalt und der späteren gesetzlichen Rente – in Deutschland im Schnitt 700–1.200 € pro Monat." },
      { q: "Wie berechne ich meine Rentenlücke?", a: "Letztes Nettogehalt minus zu erwartende Nettorente. Im Rentenbescheid der DRV findest Du die Hochrechnung." },
      { q: "Wie schließe ich die Rentenlücke?", a: "Mit privater und betrieblicher Altersvorsorge – z. B. ETF-Sparplan, bAV, Rürup oder Fondspolice." },
    ],
  },

  "riester-rente": {
    blogLinks: [{ path: "/blog/riester-letzte-chance-2026", label: "Riester letzte Chance 2026 – jetzt noch abschließen?" }],
    faqs: [
      { q: "Was ist die Riester-Rente?", a: "Eine staatlich geförderte private Altersvorsorge für Arbeitnehmer und Beamte mit Grundzulage und Kinderzulagen." },
      { q: "Lohnt sich Riester noch?", a: "Für Familien mit Kindern und Geringverdiener oft ja – für viele andere ist das ab 2027 startende Altersvorsorgedepot attraktiver." },
      { q: "Wie hoch ist die Riester-Förderung?", a: "175 € Grundzulage pro Jahr plus 300 € pro Kind (185 € für vor 2008 geborene Kinder)." },
      { q: "Was passiert mit Riester ab 2027?", a: "Bestandsverträge laufen weiter. Neuverträge werden voraussichtlich nicht mehr abschließbar sein – an die Stelle tritt das Altersvorsorgedepot." },
    ],
  },

  risikolebensversicherung: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was ist eine Risikolebensversicherung?", a: "Eine reine Todesfallabsicherung: Stirbt die versicherte Person, erhalten die Hinterbliebenen eine vereinbarte Summe." },
      { q: "Wer braucht sie?", a: "Familien mit Kindern, Paare mit gemeinsamem Kredit oder Geschäftspartner mit finanzieller Abhängigkeit." },
      { q: "Wie hoch sollte die Versicherungssumme sein?", a: "Faustregel: 5–7-faches Bruttojahreseinkommen oder Restschuld der Immobilie plus Zukunftsbedarf der Familie." },
    ],
  },

  "ruerup-rente": {
    blogLinks: [{ path: "/blog/rurup-rente-steuern-sparen", label: "Rürup-Rente – Steuern sparen mit der Basisrente" }],
    faqs: [
      { q: "Was ist die Rürup-Rente?", a: "Die Basisrente ist eine staatlich geförderte private Altersvorsorge, primär für Selbstständige und Gutverdiener." },
      { q: "Für wen lohnt sich Rürup?", a: "Vor allem für Selbstständige ohne gesetzliche Rentenversicherung und Angestellte mit hoher Steuerlast." },
      { q: "Wie viel kann ich von der Steuer absetzen?", a: "Beiträge bis 30.826 € (Single, 2026) bzw. 61.652 € (Verheiratete) sind zu 100 % als Sonderausgaben absetzbar (§ 10 Abs. 1 Nr. 2 EStG)." },
      { q: "Kann ich die Rürup-Rente kündigen?", a: "Nein. Sie ist nicht kündbar, nicht beleihbar und nicht vererbbar (außer an Ehepartner und Kinder) – nur beitragsfrei stellen ist möglich." },
    ],
  },

  sparerpauschbetrag: {
    blogLinks: [
      { path: "/blog/freistellungsauftrag-richtig-nutzen", label: "Freistellungsauftrag richtig nutzen" },
      { path: "/blog/vorabpauschale-2024-etf", label: "Vorabpauschale bei ETFs – so funktioniert sie" },
    ],
    faqs: [
      { q: "Was ist der Sparerpauschbetrag?", a: "Der jährliche Steuerfreibetrag für Kapitalerträge: 1.000 € pro Person, 2.000 € bei Ehepaaren (2026)." },
      { q: "Wie nutze ich ihn optimal?", a: "Mit einem Freistellungsauftrag bei Deiner Bank – Du kannst ihn auf mehrere Institute aufteilen." },
      { q: "Was passiert, wenn ich ihn überschreite?", a: "Auf darüber liegende Erträge fällt automatisch Abgeltungssteuer (25 % + Soli + ggf. Kirchensteuer) an." },
    ],
  },

  sparplan: {
    blogLinks: [{ path: "/blog/sparplan-vs-einmalanlage", label: "Sparplan oder Einmalanlage?" }],
    faqs: [
      { q: "Was ist ein Sparplan?", a: "Eine automatisierte regelmäßige Investition eines festen Betrags – z. B. monatlich 100 € in einen ETF." },
      { q: "ETF-Sparplan oder Einmalanlage?", a: "Statistisch schlägt die Einmalanlage den Sparplan langfristig leicht – psychologisch und für laufendes Einkommen ist der Sparplan oft besser." },
      { q: "Ab welchem Betrag lohnt sich ein Sparplan?", a: "Schon ab 25 € pro Monat. Wichtig ist Regelmäßigkeit und langer Anlagehorizont." },
    ],
  },

  "vermoegenswirksame-leistungen": {
    blogLinks: [{ path: "/blog/staatliche-foerderungen-altersvorsorge-2026", label: "Staatliche Förderungen für Deine Altersvorsorge" }],
    faqs: [
      { q: "Was sind vermögenswirksame Leistungen?", a: "Zahlungen Deines Arbeitgebers von bis zu 40 € pro Monat in einen geförderten Sparvertrag." },
      { q: "Habe ich Anspruch auf VL?", a: "Viele Tarif- und Arbeitsverträge sehen VL vor – einfach beim Arbeitgeber nachfragen." },
      { q: "In welche Anlageform investiere ich VL am besten?", a: "Für die meisten ist ein ETF-Sparplan die renditestärkste Option – Bausparen lohnt nur bei konkreten Bauplänen." },
    ],
  },

  zinseszinseffekt: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-mit-25", label: "Vermögensaufbau mit 25 – früh starten zahlt sich aus" }],
    faqs: [
      { q: "Was ist der Zinseszins?", a: "Der Effekt, bei dem Zinsen auf bereits erhaltene Zinsen berechnet werden – Kapital wächst exponentiell." },
      { q: "Wie nutze ich den Zinseszins für mich?", a: "Früh anfangen, lange dranbleiben und Erträge konsequent reinvestieren – z. B. über thesaurierende ETFs." },
      { q: "Warum ist frühes Investieren so wichtig?", a: "Bei 7 % Rendite verdoppelt sich Dein Kapital alle 10 Jahre. Wer 10 Jahre früher startet, hat am Ende oft doppelt so viel Vermögen." },
    ],
  },

  // ===== Batch 2 — additional terms =====

  aktie: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist eine Aktie?", a: "Ein Anteilsschein an einem Unternehmen – als Aktionär bist Du Miteigentümer und profitierst von Kursgewinnen und Dividenden." },
      { q: "Wie kaufe ich Aktien?", a: "Über ein Depot bei einem Online-Broker. Du wählst die Aktie aus, gibst Stückzahl und Kursgrenze an – fertig." },
      { q: "Sind Aktien riskanter als ETFs?", a: "Ja. Einzelaktien sind nicht diversifiziert, ein ETF bündelt viele Unternehmen. Für Einsteiger sind ETFs die bessere Wahl." },
      { q: "Wie werden Aktiengewinne besteuert?", a: "Kursgewinne und Dividenden unterliegen der Abgeltungssteuer (25 % + Soli + ggf. Kirchensteuer)." },
    ],
  },

  altersrente: {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Ab wann bekomme ich Altersrente?", a: "Die Regelaltersgrenze liegt für die meisten Jahrgänge bei 67 Jahren. Wer 1964 oder später geboren ist, geht regulär mit 67 in Rente." },
      { q: "Wie hoch ist meine Altersrente?", a: "Sie ergibt sich aus Deinen Entgeltpunkten multipliziert mit dem aktuellen Rentenwert (2026 ca. 39,32 € pro Punkt)." },
      { q: "Kann ich früher in Rente gehen?", a: "Ja, ab 63 mit Abschlägen von 0,3 % pro Monat. Mit 45 Beitragsjahren ist ein abschlagsfreier Rentenbeginn früher möglich." },
    ],
  },

  bafin: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was ist die BaFin?", a: "Die Bundesanstalt für Finanzdienstleistungsaufsicht – die deutsche Aufsichtsbehörde für Banken, Versicherungen und Finanzdienstleister." },
      { q: "Warum ist die BaFin wichtig?", a: "Sie sorgt für Stabilität und Integrität am Finanzmarkt und schützt Verbraucher vor unseriösen Anbietern." },
      { q: "Wie prüfe ich, ob ein Berater BaFin-registriert ist?", a: "Über das öffentliche Vermittlerregister der IHK bzw. die BaFin-Datenbank online – beides kostenlos." },
    ],
  },

  beitragsbemessungsgrenze: {
    blogLinks: [
      { path: "/blog/beitragsbemessungsgrenze-2026", label: "Beitragsbemessungsgrenzen 2026" },
      { path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" },
    ],
    faqs: [
      { q: "Was ist die Beitragsbemessungsgrenze?", a: "Die Einkommensgrenze, bis zu der Sozialversicherungsbeiträge berechnet werden. 2026 gelten bundeseinheitlich 8.450 €/Monat (RV/ALV) bzw. 5.812,50 €/Monat (GKV/PV)." },
      { q: "Was passiert mit Einkommen darüber?", a: "Auf Einkommen oberhalb der Grenze fallen keine zusätzlichen Beiträge – aber auch keine zusätzlichen Rentenpunkte mehr an." },
      { q: "Wie beeinflusst sie meine Rente?", a: "Maximal kannst Du knapp 2 Entgeltpunkte pro Jahr sammeln. Wer mehr verdient, sollte zusätzlich privat vorsorgen." },
    ],
  },

  bruttolohn: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" }],
    faqs: [
      { q: "Was ist der Bruttolohn?", a: "Das vertraglich vereinbarte Gehalt vor Abzug von Steuern und Sozialabgaben." },
      { q: "Wie viel bleibt netto vom Brutto?", a: "Je nach Steuerklasse und Einkommen 55–70 %. Faustregel: rund zwei Drittel des Bruttos landen netto." },
      { q: "Wie erhöhe ich mein Nettoeinkommen?", a: "Über Entgeltumwandlung, VL, steuerfreie Sachbezüge und das Ausschöpfen aller Werbungskosten." },
    ],
  },

  "cost-average-effekt": {
    blogLinks: [{ path: "/blog/sparplan-vs-einmalanlage", label: "Sparplan oder Einmalanlage?" }],
    faqs: [
      { q: "Was ist der Cost-Average-Effekt?", a: "Der Vorteil regelmäßiger gleich hoher Investitionen: bei niedrigen Kursen werden mehr Anteile gekauft, bei hohen weniger." },
      { q: "Warum ist er beim ETF-Sparplan vorteilhaft?", a: "Er nimmt Dir die Sorge vor dem perfekten Einstiegszeitpunkt und glättet Marktschwankungen über die Zeit." },
      { q: "Gibt es auch Nachteile?", a: "Bei langfristig steigenden Märkten schlägt eine Einmalanlage den Sparplan statistisch im Schnitt – Geld liegt dann zu lange unangelegt." },
    ],
  },

  festgeld: {
    blogLinks: [{ path: "/blog/etf-vs-tagesgeld-2024", label: "ETF vs. Tagesgeld – was lohnt sich mehr?" }],
    faqs: [
      { q: "Was ist Festgeld?", a: "Eine Geldanlage mit festem Zinssatz und fester Laufzeit – das Kapital ist während der Laufzeit nicht verfügbar." },
      { q: "Festgeld oder Tagesgeld?", a: "Tagesgeld bietet volle Flexibilität, Festgeld meist etwas höhere Zinsen. Notreserve gehört auf Tagesgeld." },
      { q: "Welche Zinsen gibt es auf Festgeld?", a: "Aktuell zwischen ca. 2,5 und 4 % p. a. – abhängig von Bank, Laufzeit und Marktphase." },
    ],
  },

  "fondsgebundene-rentenversicherung": {
    blogLinks: [{ path: "/blog/fondsgebundene-rentenversicherung", label: "Fondsgebundene Rentenversicherung – Vor- und Nachteile" }],
    faqs: [
      { q: "Was ist eine fondsgebundene Rentenversicherung?", a: "Eine private Rentenversicherung, deren Beiträge in Investmentfonds oder ETFs investiert werden – mit Steuervorteilen im Versicherungsmantel." },
      { q: "Lohnt sie sich?", a: "Bei langer Laufzeit, niedrigen Kosten und Auszahlung nach 62/12 Jahren oft ja. Entscheidend sind die Kostenquoten." },
      { q: "Was passiert, wenn die Fonds schlecht laufen?", a: "Du trägst das Kapitalmarktrisiko. Bei langem Anlagehorizont gleichen sich Schwankungen historisch meist aus." },
    ],
  },

  gkv: {
    blogLinks: [{ path: "/blog/private-krankenversicherung-wechsel", label: "Private Krankenversicherung – Wechsel sinnvoll?" }],
    faqs: [
      { q: "Was ist die GKV?", a: "Die gesetzliche Krankenversicherung – Pflichtversicherung für Arbeitnehmer bis zur Versicherungspflichtgrenze." },
      { q: "Wie hoch ist der GKV-Beitrag?", a: "Allgemeiner Beitragssatz 14,6 % plus kassenindividueller Zusatzbeitrag (1,7–3,8 % in 2026)." },
      { q: "Was ist der Unterschied zwischen GKV und PKV?", a: "GKV ist solidarisch und einkommensabhängig, PKV kalkuliert nach Alter, Gesundheit und gewünschten Leistungen." },
    ],
  },

  haftpflichtversicherung: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was deckt die Haftpflichtversicherung ab?", a: "Schäden, die Du anderen Personen unbeabsichtigt zufügst – Personen-, Sach- und Vermögensschäden." },
      { q: "Wer braucht eine Haftpflichtversicherung?", a: "Jeder Erwachsene. Ohne sie haftest Du nach deutschem Recht unbegrenzt mit Deinem gesamten Vermögen." },
      { q: "Wie hoch sollte die Deckungssumme sein?", a: "Mindestens 10 Mio. € pauschal für Personen- und Sachschäden – der Mehrpreis gegenüber niedrigeren Summen ist minimal." },
    ],
  },

  "homeoffice-pauschale": {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" }],
    faqs: [
      { q: "Was ist die Homeoffice-Pauschale?", a: "6 € pro Tag im Homeoffice, steuerlich absetzbar – maximal 1.260 € pro Jahr (210 Tage, Stand 2026)." },
      { q: "Wie setze ich sie ab?", a: "Als Werbungskosten in Anlage N Deiner Einkommensteuererklärung." },
      { q: "Brauche ich einen Nachweis?", a: "Ein separater Nachweis ist nicht erforderlich – eine schlichte Aufstellung Deiner Homeoffice-Tage reicht aus." },
    ],
  },

  immobilienkredit: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Wie funktioniert ein Immobilienkredit?", a: "Ein zweckgebundenes Darlehen zur Finanzierung von Wohneigentum – die Immobilie selbst dient als Sicherheit." },
      { q: "Wie viel Eigenkapital brauche ich?", a: "Faustregel: mindestens 10–20 % des Kaufpreises plus die Kaufnebenkosten (Notar, Grunderwerbsteuer, ggf. Makler)." },
      { q: "Was ist die Sollzinsbindung?", a: "Der Zeitraum, in dem der vereinbarte Zinssatz fest gilt – meist 5, 10, 15 oder 20 Jahre." },
    ],
  },

  "jaehrliche-renteninformation": {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Was steht in der Renteninformation?", a: "Die bisher erreichte Rente, die hochgerechnete Rente und Hinweise zur Kaufkraft nach Inflation." },
      { q: "Wie lese ich sie richtig?", a: "Achte vor allem auf den realen Wert nach Inflation – er zeigt, was Deine Rente in heutiger Kaufkraft wert ist." },
      { q: "Was tun, wenn die Rente zu niedrig ist?", a: "Frühzeitig private und betriebliche Vorsorge aufbauen – z. B. ETF-Sparplan, bAV oder Rürup." },
    ],
  },

  kaskoversicherung: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was ist der Unterschied zwischen Teil- und Vollkasko?", a: "Teilkasko deckt Diebstahl, Brand, Hagel, Wildunfall und Glasbruch. Vollkasko zusätzlich selbstverschuldete Unfälle." },
      { q: "Wann lohnt sich Vollkasko?", a: "Bei Neu- und Jungwagen sowie bei laufender Finanzierung. Bei älteren Fahrzeugen reicht meist Teilkasko." },
      { q: "Was ist der Selbstbehalt?", a: "Der Betrag, den Du im Schadensfall selbst trägst – üblich sind 150–500 €. Höherer Selbstbehalt senkt den Beitrag." },
    ],
  },

  kinderzulage: {
    blogLinks: [{ path: "/blog/riester-letzte-chance-2026", label: "Riester letzte Chance 2026 – jetzt noch abschließen?" }],
    faqs: [
      { q: "Was ist die Kinderzulage bei Riester?", a: "Ein staatlicher Zuschuss von 300 € pro Jahr und Kind (ab 2008 geboren) bzw. 185 € (vor 2008)." },
      { q: "Wie beantrage ich sie?", a: "Über einen einmaligen Dauerzulagenantrag bei Deinem Riester-Anbieter – sie wird dann automatisch jährlich beantragt." },
      { q: "Wer bekommt sie?", a: "Der Elternteil, dem das Kindergeld ausgezahlt wird – in der Regel die Mutter." },
    ],
  },

  lohnsteuer: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" }],
    faqs: [
      { q: "Was ist die Lohnsteuer?", a: "Die Einkommensteuer auf Arbeitseinkommen – direkt vom Arbeitgeber einbehalten und ans Finanzamt abgeführt." },
      { q: "Wie wird sie berechnet?", a: "Auf Basis von Bruttogehalt, Steuerklasse, Freibeträgen und ggf. Kirchensteuermerkmal." },
      { q: "Wie spare ich Lohnsteuer?", a: "Über Werbungskosten, Sonderausgaben, Entgeltumwandlung und das gezielte Nutzen aller Freibeträge." },
    ],
  },

  lohnsteuererklaerung: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" }],
    faqs: [
      { q: "Muss ich eine Steuererklärung machen?", a: "Pflicht z. B. bei Lohnersatzleistungen über 410 €, Nebeneinkünften über 410 € oder Steuerklassenkombination 3/5." },
      { q: "Was bekomme ich zurück?", a: "Im Schnitt rund 1.095 € pro Jahr (Veranlagungszeitraum 2020, Destatis) – mit gezielter Optimierung oft deutlich mehr." },
      { q: "Was kann ich absetzen?", a: "Werbungskosten, Sonderausgaben, außergewöhnliche Belastungen und haushaltsnahe Dienstleistungen." },
    ],
  },

  notfallreserve: {
    blogLinks: [{ path: "/blog/geldanlage-einsteiger-guide", label: "Geldanlage für Einsteiger" }],
    faqs: [
      { q: "Wie groß sollte die Notfallreserve sein?", a: "Faustregel: 3 Nettomonatsgehälter für Angestellte, 6 Monate für Selbstständige." },
      { q: "Wo parke ich sie am besten?", a: "Auf einem Tagesgeldkonto – täglich verfügbar und gesetzlich bis 100.000 € abgesichert." },
      { q: "Ab wann fange ich an zu investieren?", a: "Sobald die Notfallreserve steht und teure Konsumkredite getilgt sind." },
    ],
  },

  pendlerpauschale: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" }],
    faqs: [
      { q: "Was ist die Pendlerpauschale?", a: "Seit dem 1. Januar 2026 gelten 0,38 € pro Kilometer einfacher Arbeitsweg – ab dem ersten Kilometer, als Werbungskosten absetzbar (Steueränderungsgesetz 2025)." },
      { q: "Wie berechne ich sie?", a: "Arbeitstage × einfache Entfernung in km × Kilometersatz. Wichtig: nicht Hin- und Rückweg doppelt zählen." },
      { q: "Gilt sie auch für öffentliche Verkehrsmittel?", a: "Ja, die Pauschale gilt unabhängig vom Verkehrsmittel. Höhere ÖPNV-Kosten können alternativ angesetzt werden." },
    ],
  },

  portfolio: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist ein Portfolio?", a: "Die Gesamtheit aller Finanzanlagen – Aktien, ETFs, Anleihen, Tagesgeld, Immobilien, Gold." },
      { q: "Wie baue ich ein gutes Portfolio auf?", a: "Breit diversifiziert, passend zu Deinem Anlagehorizont und Risikoprofil. Ein Welt-ETF ist oft der Kern." },
      { q: "Was gehört in ein Einsteiger-Portfolio?", a: "Notfallreserve auf Tagesgeld plus ein breit gestreuter Welt-ETF wie MSCI World oder MSCI ACWI." },
    ],
  },

  rendite: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist Rendite?", a: "Der prozentuale Gewinn einer Geldanlage bezogen auf das eingesetzte Kapital, meist angegeben pro Jahr (p. a.)." },
      { q: "Was ist eine gute Rendite?", a: "Realistisch und langfristig: 7–10 % p. a. mit breit gestreuten Aktien-ETFs, 2–4 % mit Anleihen, 2–4 % mit Tagesgeld." },
      { q: "Wie berechne ich die Rendite?", a: "(Endwert − Anfangswert + Erträge) ÷ Anfangswert × 100. Bei mehrjährigen Anlagen mit der annualisierten Formel rechnen." },
    ],
  },

  rentenpunkte: {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Was sind Rentenpunkte?", a: "Die Währung der gesetzlichen Rentenversicherung. Wer ein Jahr lang das Durchschnittseinkommen verdient, erhält genau einen Punkt." },
      { q: "Wie viele habe ich?", a: "Dein aktueller Stand steht in der jährlichen Renteninformation der Deutschen Rentenversicherung." },
      { q: "Wie viel ist ein Rentenpunkt wert?", a: "2026 ca. 39,32 € pro Punkt und Monat – jährlich angepasst über den Rentenwert." },
    ],
  },

  solidaritaetszuschlag: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" }],
    faqs: [
      { q: "Was ist der Solidaritätszuschlag?", a: "Eine Zusatzsteuer von 5,5 % auf die Einkommen- bzw. Lohnsteuer." },
      { q: "Wer zahlt ihn noch?", a: "Seit 2021 nur noch Gutverdiener mit einem zu versteuernden Einkommen über rund 62.603 € (Single, 2026) – auf Kapitalerträge gilt er weiterhin für alle." },
      { q: "Wie hoch ist er?", a: "5,5 % auf die festgesetzte Einkommensteuer – bei Kapitalerträgen also rund 1,375 % vom Bruttoertrag." },
    ],
  },

  steuerklasse: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" }],
    faqs: [
      { q: "Was sind die Steuerklassen?", a: "Klasse 1: Singles, 2: Alleinerziehende, 3/5 oder 4/4: Ehepaare, 6: Zweitjobs." },
      { q: "Welche Steuerklasse ist für mich am besten?", a: "Bei stark unterschiedlichen Einkommen oft 3/5, bei ähnlichen Einkommen 4/4. Über die Jahressteuererklärung wird ohnehin endgültig abgerechnet." },
      { q: "Kann ich die Steuerklasse wechseln?", a: "Ja, mehrmals pro Jahr beim Finanzamt – z. B. nach Heirat, Trennung oder Elternzeit." },
    ],
  },

  unfallversicherung: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was deckt die Unfallversicherung ab?", a: "Bleibende körperliche Beeinträchtigungen durch Unfälle – 24/7, weltweit, auch in der Freizeit." },
      { q: "Brauche ich eine private Unfallversicherung?", a: "Sinnvoll für Familien mit Kindern, Sportler und Hausfrauen/-männer. Sie ersetzt aber keine Berufsunfähigkeitsversicherung." },
      { q: "Was ist der Unterschied zur Berufsunfähigkeitsversicherung?", a: "Die Unfallversicherung leistet nur bei Unfällen, die Berufsunfähigkeitsversicherung bei jeder Ursache (auch Krankheit) der Berufsunfähigkeit." },
    ],
  },

  "vorauszahlung-steuer": {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter" }],
    faqs: [
      { q: "Was sind Steuervorauszahlungen?", a: "Vierteljährliche Vorauszahlungen auf die voraussichtliche Einkommensteuer, vom Finanzamt anhand der Vorjahressteuer festgesetzt." },
      { q: "Wer muss sie zahlen?", a: "Selbstständige sowie Arbeitnehmer mit nennenswerten Nebeneinkünften (z. B. aus Vermietung oder Kapital)." },
      { q: "Wie vermeide ich Nachzahlungen?", a: "Eine realistische Anpassung der Vorauszahlungen beim Finanzamt beantragen, sobald sich Deine Einkünfte deutlich ändern." },
    ],
  },

  versicherungspflichtgrenze: {
    blogLinks: [{ path: "/blog/private-krankenversicherung-wechsel", label: "Private Krankenversicherung – Wechsel sinnvoll?" }],
    faqs: [
      { q: "Was ist die Versicherungspflichtgrenze 2026?", a: "77.400 € Bruttojahresgehalt (6.450 €/Monat). Ab dieser Grenze dürfen Arbeitnehmer in die PKV wechseln. Für Bestands-PKV-Versicherte (seit vor dem 1.1.2003) gilt die niedrigere besondere JAEG von 69.750 €/Jahr." },
      { q: "Was passiert, wenn ich sie überschreite?", a: "Du kannst weiter in der GKV bleiben oder in die PKV wechseln – ein automatischer Wechsel erfolgt nicht." },
      { q: "Muss ich sofort wechseln?", a: "Nein. Lass Dich unabhängig beraten – ein PKV-Wechsel ist langfristig zu betrachten, ein Rückwechsel ab 55 oft nicht mehr möglich." },
    ],
  },

  witwenrente: {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Was ist die Witwenrente?", a: "Eine Leistung der gesetzlichen Rentenversicherung an überlebende Ehepartner und eingetragene Lebenspartner." },
      { q: "Wie hoch ist sie?", a: "Große Witwenrente: 55 % der Rente des Verstorbenen. Kleine Witwenrente: 25 %, befristet auf 24 Monate." },
      { q: "Was ist der Unterschied zwischen großer und kleiner Witwenrente?", a: "Die große wird ab dem 47. Lebensjahr oder bei Erwerbsminderung gezahlt, die kleine darunter und nur befristet." },
    ],
  },

  "zusatzbeitrag-gkv": {
    blogLinks: [{ path: "/blog/private-krankenversicherung-wechsel", label: "Private Krankenversicherung – Wechsel sinnvoll?" }],
    faqs: [
      { q: "Was ist der GKV-Zusatzbeitrag?", a: "Ein kassenindividueller Aufschlag auf den allgemeinen GKV-Beitragssatz von 14,6 %." },
      { q: "Wie hoch ist er?", a: "2026 zwischen rund 1,7 % und 3,8 %, je nach Krankenkasse." },
      { q: "Lohnt sich ein Krankenkassenwechsel?", a: "Ja, oft mehrere hundert Euro pro Jahr Ersparnis – bei vergleichbarem Leistungsumfang." },
    ],
  },

  "zwei-saeulen-modell": {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Was ist das Drei-Säulen-Modell der Altersvorsorge?", a: "Gesetzliche Rente (1. Säule), betriebliche Altersvorsorge (2. Säule) und private Vorsorge (3. Säule) – als sich ergänzende Bausteine." },
      { q: "Welche Säulen gibt es?", a: "Erste: gesetzliche Rente. Zweite: bAV. Dritte: private Vorsorge wie ETF-Sparplan, Rürup oder Fondspolice." },
      { q: "Welche ist am wichtigsten?", a: "Alle. Die gesetzliche Rente reicht für die meisten nicht – ergänzende Säulen sind heute praktisch Pflicht." },
    ],
  },
  anleihe: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist eine Anleihe?", a: "Ein Wertpapier, mit dem Du einem Staat oder Unternehmen Geld leihst und dafür Zinsen erhältst." },
      { q: "Sind Anleihen sicher?", a: "Staatsanleihen mit guter Bonität (z. B. Deutschland) gelten als sehr sicher. Unternehmensanleihen sind risikoreicher, bieten aber höhere Zinsen." },
      { q: "Wie investiere ich in Anleihen?", a: "Am einfachsten über Anleihen-ETFs – diese bündeln viele Anleihen und sind günstig handelbar." },
    ],
  },

  arbeitgeberzuschuss: {
    blogLinks: [{ path: "/blog/betriebliche-altersvorsorge-steuerfrei", label: "Betriebliche Altersvorsorge steuerfrei nutzen" }],
    faqs: [
      { q: "Was ist der Arbeitgeberzuschuss zur bAV?", a: "Ein gesetzlich vorgeschriebener Zuschuss von mindestens 15 % auf den umgewandelten Bruttobetrag, sofern der Arbeitgeber Sozialabgaben spart." },
      { q: "Wie hoch ist er?", a: "Mindestens 15 %, viele Arbeitgeber zahlen freiwillig 20 % oder mehr." },
      { q: "Hat mein Arbeitgeber eine Pflicht dazu?", a: "Ja – für alle Entgeltumwandlungen seit 2019 (Neuverträge) und seit 2022 auch für Bestandsverträge." },
    ],
  },

  aussteuerung: {
    blogLinks: [{ path: "/blog/krankentagegeld-luecke-absicherung", label: "Krankentagegeld – die Lücke zum Netto absichern" }],
    faqs: [
      { q: "Was ist die Aussteuerung?", a: "Das Ende des Krankengeldbezugs nach 78 Wochen Krankheit – danach gibt es kein Krankengeld von der GKV mehr." },
      { q: "Was passiert nach 78 Wochen Krankheit?", a: "Ohne weitere Absicherung musst Du den Lebensunterhalt selbst tragen oder Erwerbsminderungsrente beantragen, die meist niedrig ausfällt." },
      { q: "Wie schütze ich mich?", a: "Mit einer Berufsunfähigkeitsversicherung und/oder einer privaten Krankentagegeldversicherung." },
    ],
  },

  benchmark: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist eine Benchmark?", a: "Ein Vergleichsindex, an dem die Wertentwicklung eines Fonds gemessen wird." },
      { q: "Warum ist sie wichtig?", a: "Sie zeigt, ob ein Fondsmanager seinen Job gut macht – oder ob ein günstiger ETF die bessere Wahl wäre." },
      { q: "Schlägt ein aktiver Fonds seine Benchmark?", a: "Langfristig schaffen das nur die wenigsten – nach Kosten meist deutlich unter 20 % der Fonds." },
    ],
  },

  bonitaet: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was ist Bonität?", a: "Die Kreditwürdigkeit einer Person oder eines Unternehmens – wie wahrscheinlich es ist, dass Kredite zurückgezahlt werden." },
      { q: "Wie verbessere ich meine Bonität?", a: "Rechnungen pünktlich zahlen, alte Kredite löschen lassen, wenige Konten und Karten, regelmäßige Schufa-Selbstauskunft prüfen." },
      { q: "Was ist die Schufa?", a: "Die wichtigste Auskunftei in Deutschland, die Bonitätsdaten sammelt und an Banken, Vermieter und Händler weitergibt." },
    ],
  },

  basisrente: {
    blogLinks: [{ path: "/blog/rurup-rente-steuern-sparen", label: "Rürup-Rente – clever Steuern sparen" }],
    faqs: [
      { q: "Was ist die Basisrente?", a: "Eine staatlich geförderte private Altersvorsorge mit hoher Steuerersparnis in der Ansparphase." },
      { q: "Für wen lohnt sie sich?", a: "Vor allem für Selbstständige ohne gesetzliche Rente und für Gutverdiener mit hohem Steuersatz." },
      { q: "Wie hoch ist die Steuerersparnis?", a: "Bei einem Spitzensteuersatz von 42 % zahlt der Staat bis zu 42 % Deines Beitrags faktisch zurück – über die Steuererstattung." },
    ],
  },

  deckungskapital: {
    blogLinks: [{ path: "/blog/fondsgebundene-rentenversicherung", label: "Fondsgebundene Rentenversicherung – Vor- und Nachteile" }],
    faqs: [
      { q: "Was ist das Deckungskapital?", a: "Das innerhalb einer Versicherung gebildete Sparkapital, das zur Finanzierung künftiger Leistungen dient." },
      { q: "Wie hoch ist mein Rückkaufswert?", a: "Er ergibt sich aus dem Deckungskapital abzüglich Stornoabschlägen – steht auf dem jährlichen Standmitteilungsschreiben." },
      { q: "Was passiert beim Kündigen einer Rentenversicherung?", a: "Du bekommst den Rückkaufswert, oft deutlich unter den gezahlten Beiträgen. Beitragsfreistellung oder Verkauf an einen Ankäufer sind häufig besser." },
    ],
  },

  direktversicherung: {
    blogLinks: [{ path: "/blog/betriebliche-altersvorsorge-steuerfrei", label: "Betriebliche Altersvorsorge steuerfrei nutzen" }],
    faqs: [
      { q: "Was ist eine Direktversicherung?", a: "Eine vom Arbeitgeber abgeschlossene Lebens- oder Rentenversicherung als Form der betrieblichen Altersvorsorge." },
      { q: "Wie unterscheidet sie sich von anderen bAV-Formen?", a: "Sie ist der einfachste und am weitesten verbreitete Durchführungsweg – besonders portabel und transparent." },
      { q: "Was passiert bei Jobwechsel?", a: "Du kannst den Vertrag mitnehmen, beim neuen Arbeitgeber fortführen oder privat weiterbesparen." },
    ],
  },

  eigenkapital: {
    blogLinks: [
      { path: "/leistungen", label: "Unsere Leistungen im Überblick" },
      { path: "/blog/immobilien-kapitalanlage-grundlagen", label: "Immobilien als Kapitalanlage – Grundlagen" },
    ],
    faqs: [
      { q: "Was ist Eigenkapital?", a: "Eigene Mittel, die Du in eine Finanzierung oder Investition einbringst – im Gegensatz zu Fremdkapital wie Krediten." },
      { q: "Wie viel Eigenkapital brauche ich für eine Immobilie?", a: "Klassisch 20–30 % des Kaufpreises, mindestens jedoch die Kaufnebenkosten (ca. 10–15 %)." },
      { q: "Kann ich ohne Eigenkapital kaufen?", a: "Ja, mit Voll- oder 110-%-Finanzierung – aber zu schlechteren Konditionen und mit höherem Risiko." },
    ],
  },

  einmalanlage: {
    blogLinks: [{ path: "/blog/sparplan-vs-einmalanlage", label: "Sparplan vs. Einmalanlage – was ist besser?" }],
    faqs: [
      { q: "Was ist eine Einmalanlage?", a: "Eine einmalige Investition eines größeren Betrags – zum Beispiel einer Abfindung, eines Erbes oder einer Bonuszahlung." },
      { q: "Einmalanlage oder Sparplan?", a: "Statistisch gewinnt langfristig die Einmalanlage. Wer Angst vor schlechtem Timing hat, kann den Betrag auf 6–12 Monate strecken." },
      { q: "Wann ist eine Einmalanlage sinnvoll?", a: "Wenn Du langfristig (>10 Jahre) investieren kannst und psychologisch mit Schwankungen umgehen kannst." },
    ],
  },


  "fire-methode": {
    blogLinks: [
      { path: "/blog/vermoegensaufbau-mit-25", label: "Vermögensaufbau mit 25 – so startest Du clever" },
      { path: "/blog/finanzielle-unabhaengigkeit-grundlagen", label: "Finanzielle Unabhängigkeit – Grundlagen" },
    ],
    faqs: [
      { q: "Was ist die FIRE-Methode?", a: "Ein Konzept, mit dem Du durch hohe Sparquote und langfristiges Investieren früh finanziell unabhängig wirst." },
      { q: "Wie viel muss ich für FIRE ansparen?", a: "Das 25-Fache Deiner jährlichen Ausgaben – nach der 4-%-Entnahmeregel." },
      { q: "Was ist die 4-%-Regel?", a: "Eine Faustregel, die besagt, dass Du jährlich 4 % Deines Portfolios entnehmen kannst, ohne es aufzubrauchen." },
    ],
  },

  freibetrag: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" }],
    faqs: [
      { q: "Was ist ein Freibetrag?", a: "Ein Betrag, bis zu dem keine Steuer anfällt – z. B. Grundfreibetrag, Sparerpauschbetrag oder Kinderfreibetrag." },
      { q: "Was ist der Unterschied zwischen Freibetrag und Freigrenze?", a: "Beim Freibetrag wird nur der Betrag oberhalb besteuert; bei der Freigrenze fällt bei Überschreiten der gesamte Betrag Steuer an." },
      { q: "Wie nutze ich Freibeträge optimal?", a: "Freistellungsauftrag stellen, Werbungskosten geltend machen, Lohnsteuerfreibetrag eintragen lassen." },
    ],
  },

  gehaltsverhandlung: {
    blogLinks: [
      { path: "/blog/gehaltstransparenz-2026-neue-rechte-arbeitnehmer", label: "Gehaltstransparenz 2026 – neue Rechte für Arbeitnehmer" },
      { path: "/blog/gehalt-investieren-wieviel-monatlich", label: "Vom Gehalt investieren – wie viel pro Monat?" },
    ],
    faqs: [
      { q: "Wie führe ich eine Gehaltsverhandlung?", a: "Vorbereitung mit Marktdaten, Leistungsnachweisen und einer klaren Zielzahl – ruhig, sachlich und mit Alternativen im Kopf." },
      { q: "Wann ist der richtige Zeitpunkt?", a: "Nach erfolgreichen Projekten, beim Jahresgespräch oder bei zusätzlichen Verantwortlichkeiten." },
      { q: "Wie viel mehr Gehalt kann ich verlangen?", a: "Im Bestandsjob meist 5–10 %, mit neuer Rolle bis 15 %, beim Jobwechsel oft 15–25 %." },
    ],
  },

  "gesetzliche-rentenversicherung": {
    blogLinks: [
      { path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" },
      { path: "/blog/generationenvertrag-rente-eigenvorsorge", label: "Generationenvertrag – warum Eigenvorsorge unverzichtbar ist" },
    ],
    faqs: [
      { q: "Wie funktioniert die gesetzliche Rente?", a: "Per Umlageverfahren: Beiträge der Arbeitnehmer finanzieren direkt die Renten der heutigen Rentner." },
      { q: "Wie hoch sind die Beiträge?", a: "18,6 % vom Bruttolohn (2026) – je zur Hälfte von Arbeitgeber und Arbeitnehmer getragen." },
      { q: "Reicht die gesetzliche Rente im Alter?", a: "Für die meisten nicht – die Standardrente liegt bei rund 48 % des durchschnittlichen Nettolohns. Eine ergänzende Vorsorge ist Pflicht." },
    ],
  },

  grundfreibetrag: {
    blogLinks: [
      { path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" },
      { path: "/blog/steuern-sparen-2026-arbeitnehmer-aenderungen", label: "Steuern sparen 2026: Änderungen für Arbeitnehmer" },
    ],
    faqs: [
      { q: "Was ist der Grundfreibetrag 2026?", a: "12.348 € für Singles bzw. 24.696 € bei Zusammenveranlagung Verheirateter." },
      { q: "Wie nutze ich ihn?", a: "Er wird automatisch berücksichtigt – sowohl bei der Lohnsteuer als auch bei der Einkommensteuererklärung." },
      { q: "Was passiert, wenn ich ihn überschreite?", a: "Nur der Teil oberhalb des Freibetrags wird besteuert – beginnend mit dem Eingangssteuersatz von 14 %." },
    ],
  },

  index: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist ein Aktienindex?", a: "Ein Korb aus Aktien, dessen Wertentwicklung den jeweiligen Markt abbildet." },
      { q: "Was ist der DAX?", a: "Der deutsche Leitindex mit den 40 größten börsennotierten Unternehmen Deutschlands." },
      { q: "Wie investiere ich in einen Index?", a: "Über einen ETF, der den Index 1:1 nachbildet – günstig, transparent und breit diversifiziert." },
    ],
  },

  "jobwechsel-altersvorsorge": {
    blogLinks: [{ path: "/blog/betriebliche-altersvorsorge-steuerfrei", label: "Betriebliche Altersvorsorge steuerfrei nutzen" }],
    faqs: [
      { q: "Was passiert mit meiner bAV beim Jobwechsel?", a: "Du kannst sie mitnehmen, beitragsfrei stellen oder privat weiterbesparen – je nach Vertrag und Konditionen." },
      { q: "Kann ich meinen Riester-Vertrag mitnehmen?", a: "Ja, Riester-Verträge sind voll portabel und laufen unverändert weiter." },
      { q: "Was sind meine Optionen?", a: "Übertragung zum neuen Arbeitgeber, Beitragsfreistellung, private Fortführung oder – in seltenen Fällen – Auszahlung." },
    ],
  },

  kirchensteuer: {
    blogLinks: [
      { path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" },
      { path: "/blog/steuerklasse-optimieren-lohnsteuer", label: "Steuerklasse optimieren und Lohnsteuer senken" },
    ],
    faqs: [
      { q: "Was ist die Kirchensteuer?", a: "Ein Steuerzuschlag, den Mitglieder einer Kirche zusätzlich zur Einkommensteuer zahlen." },
      { q: "Wie hoch ist sie?", a: "8 % in Bayern und Baden-Württemberg, 9 % in den übrigen Bundesländern." },
      { q: "Kann ich sie von der Steuer absetzen?", a: "Ja, vollständig als Sonderausgabe in der Einkommensteuererklärung." },
    ],
  },

  krankengeld: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was ist Krankengeld?", a: "Eine Lohnersatzleistung der GKV bei längerer Krankheit ab der 7. Woche." },
      { q: "Wie hoch ist es?", a: "70 % des Bruttogehalts, höchstens jedoch 90 % des Nettogehalts." },
      { q: "Wie lange bekomme ich Krankengeld?", a: "Maximal 78 Wochen innerhalb von 3 Jahren wegen derselben Krankheit." },
    ],
  },

  liquiditaet: {
    blogLinks: [{ path: "/blog/geldanlage-einsteiger-guide", label: "Geldanlage für Einsteiger – der komplette Guide" }],
    faqs: [
      { q: "Was bedeutet Liquidität?", a: "Die Fähigkeit, einen Vermögenswert kurzfristig und ohne große Verluste in Bargeld umzuwandeln." },
      { q: "Wie viel Liquidität brauche ich?", a: "Faustregel: 3–6 Nettomonatsgehälter als Notgroschen auf einem Tagesgeldkonto." },
      { q: "Liquid oder investiert – was ist besser?", a: "Beides. Notgroschen liquid halten, alles darüber langfristig investieren." },
    ],
  },

  minijob: {
    blogLinks: [
      { path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" },
      { path: "/blog/minijob-rentenversicherung-2026", label: "Minijob und Rente 2026" },
    ],
    faqs: [
      { q: "Was ist ein Minijob?", a: "Eine geringfügige Beschäftigung mit maximal 602 € Monatsverdienst (2026). Die Grenze folgt dem Mindestlohn (13,90 € × 130 ÷ 3)." },
      { q: "Welche Versicherungen habe ich als Minijobber?", a: "Nur die gesetzliche Rentenversicherung (auf Wunsch abwählbar). Krankenversicherung musst Du anders absichern (z. B. Familienversicherung)." },
      { q: "Baut ein Minijob Rentenansprüche auf?", a: "Ja, in geringem Umfang – sofern Du nicht von der Versicherungspflicht befreit bist." },
    ],
  },

  musterportfolio: {
    blogLinks: [{ path: "/blog/etf-portfolio-aufbauen-anleitung", label: "ETF-Portfolio aufbauen – Schritt für Schritt" }],
    faqs: [
      { q: "Was ist ein gutes Einsteiger-Portfolio?", a: "Ein einziger weltweit gestreuter ETF (z. B. MSCI World oder FTSE All-World) reicht für die meisten Anleger völlig aus." },
      { q: "Wie viele ETFs brauche ich?", a: "Einer reicht für den Start. Zwei bis vier ETFs erlauben mehr Feintuning, mehr ist meist unnötig." },
      { q: "Was ist das Weltportfolio?", a: "Eine breit gestreute Mischung aus globalen Aktien- und ggf. Anleihen-ETFs – populär gemacht von Gerd Kommer." },
    ],
  },

  nachlassplanung: {
    blogLinks: [{ path: "/blog/vermoegen-vererben-schenkung-erbschaft", label: "Vermögen vererben – Schenkung und Erbschaft klug regeln" }],
    faqs: [
      { q: "Was ist Nachlassplanung?", a: "Die vorausschauende Regelung, was im Todesfall mit Deinem Vermögen geschehen soll." },
      { q: "Wie spare ich Erbschaftssteuer?", a: "Durch Nutzung der Schenkungsfreibeträge (alle 10 Jahre), Testamente und – bei größerem Vermögen – Familienstiftungen." },
      { q: "Brauche ich ein Testament?", a: "Ja, wenn Du von der gesetzlichen Erbfolge abweichen möchtest oder unverheiratet zusammenlebst." },
    ],
  },

  nebeneinkuenfte: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" }],
    faqs: [
      { q: "Wie werden Nebeneinkünfte versteuert?", a: "Mit Deinem persönlichen Einkommensteuersatz, sobald sie die Freigrenze von 410 € überschreiten." },
      { q: "Bis wann sind sie steuerfrei?", a: "Bis 410 € jährlich greift der Härteausgleich, danach sind sie voll steuerpflichtig." },
      { q: "Muss ich eine Steuererklärung machen?", a: "Ja – bei Nebeneinkünften über 410 € jährlich besteht in der Regel Abgabepflicht." },
    ],
  },

  rentenalter: {
    blogLinks: [
      { path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" },
      { path: "/blog/rentenerhoehung-2026-steuerpflicht-rentner", label: "Rentenerhöhung 2026 – Steuerpflicht für Rentner" },
      { path: "/blog/aktivrente-2026-steuerfrei-dazuverdienen", label: "Aktivrente 2026 – steuerfrei dazuverdienen" },
    ],
    faqs: [
      { q: "Wann kann ich in Rente?", a: "Regulär mit 67 (Jahrgänge ab 1964). Mit 45 Beitragsjahren bereits mit 65 ohne Abschläge." },
      { q: "Was kostet frühzeitige Verrentung?", a: "0,3 % Abschlag pro vorgezogenem Monat – also bis zu 14,4 % bei 4 Jahren früher." },
      { q: "Wie rechnet sich Frührente?", a: "Nur, wenn Du die Lücke privat geschlossen hast oder die Lebenserwartung gesundheitlich begrenzt ist." },
    ],
  },

  rohstoffe: {
    blogLinks: [{ path: "/blog/inflationsschutz-sachwertinvestments-2024", label: "Inflationsschutz mit Sachwert-Investments 2024" }],
    faqs: [
      { q: "Wie investiere ich in Rohstoffe?", a: "Über Rohstoff-ETFs/ETCs, Aktien von Rohstoffunternehmen oder physisch (z. B. Gold)." },
      { q: "Ist Gold eine gute Geldanlage?", a: "Als Beimischung (5–10 %) zur Diversifikation ja – als Hauptinvestment eher nein, da es keine Zinsen oder Dividenden bringt." },
      { q: "Rohstoffe im ETF-Portfolio?", a: "Eine kleine Beimischung (5–10 %) ist sinnvoll – etwa über einen breiten Rohstoff-ETF." },
    ],
  },

  schufa: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was ist die Schufa?", a: "Eine private Auskunftei, die Bonitätsdaten sammelt und an Banken, Vermieter und Händler weitergibt." },
      { q: "Wie verbessere ich meinen Schufa-Score?", a: "Rechnungen pünktlich zahlen, ungenutzte Konten/Karten schließen, regelmäßig Datenkopie prüfen, falsche Einträge korrigieren lassen." },
      { q: "Kann ich falsche Einträge löschen lassen?", a: "Ja, falsche oder veraltete Einträge müssen auf Antrag korrigiert oder gelöscht werden – nutze die kostenlose Datenkopie." },
    ],
  },

  sozialversicherung: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" }],
    faqs: [
      { q: "Was ist die Sozialversicherung?", a: "Ein staatliches System aus fünf Pflichtversicherungen, das Arbeitnehmer gegen die großen Lebensrisiken absichert." },
      { q: "Wie hoch sind die Beiträge?", a: "2026 zusammen rund 42,3 % vom Bruttolohn (mit Kind) bzw. 42,9 % (kinderlos): RV 18,6 %, ALV 2,6 %, KV 14,6 % + Ø-Zusatzbeitrag 2,9 %, PV 3,6 % / 4,2 %. Arbeitnehmer und Arbeitgeber teilen paritätisch — Ausnahme: den 0,6-%-Zuschlag für Kinderlose in der PV trägt der Arbeitnehmer allein." },
      { q: "Was bekomme ich dafür?", a: "Gesetzliche Rente, Krankenversicherung, Pflegeleistungen, Arbeitslosengeld und Schutz bei Arbeitsunfällen." },
    ],
  },

  steuererklaerung: {
    blogLinks: [
      { path: "/blog/steuererklarung-2024-vergessene-ausgaben", label: "Steuererklärung – häufig vergessene Ausgaben" },
      { path: "/blog/steuererklarung-2025-fehler-vermeiden", label: "Steuererklärung – häufige Fehler vermeiden" },
    ],
    faqs: [
      { q: "Muss ich eine Steuererklärung machen?", a: "Pflicht bei Nebeneinkünften >410 €, Lohnersatzleistungen >410 €, mehreren Arbeitgebern oder Steuerklassenkombination III/V." },
      { q: "Bis wann muss sie abgegeben werden?", a: "Pflicht: 31. Juli des Folgejahres. Mit Steuerberater: bis Ende Februar des übernächsten Jahres." },
      { q: "Was bekomme ich zurück?", a: "Im Schnitt rund 1.095 € (Veranlagungszeitraum 2020, Destatis) – durch Werbungskosten, Sonderausgaben, Pendlerpauschale, Homeoffice-Pauschale u. a." },
    ],
  },

  tilgung: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was ist die Tilgung?", a: "Der Teil Deiner Kreditrate, der den eigentlichen Schuldenstand reduziert – getrennt von den Zinsen." },
      { q: "Wie hoch sollte die Tilgung sein?", a: "Mindestens 2–3 %, in Niedrigzinsphasen besser 3–4 %, um den Kredit in akzeptabler Zeit zurückzuführen." },
      { q: "Sondertilgung – was ist das?", a: "Eine zusätzliche Rückzahlung außerhalb der vereinbarten Monatsrate – meist bis 5 % der Kreditsumme pro Jahr ohne Aufpreis möglich." },
    ],
  },

  urlaubsgeld: {
    blogLinks: [{ path: "/blog/urlaubsgeld-2026-steuern-erholungsbeihilfe", label: "Urlaubsgeld 2026 – Steuern & Erholungsbeihilfe" }],
    faqs: [
      { q: "Ist Urlaubsgeld steuerpflichtig?", a: "Ja, voll steuer- und sozialabgabenpflichtig – wie ein normales Monatsgehalt." },
      { q: "Was ist die Erholungsbeihilfe?", a: "Eine pauschal versteuerbare (25 %) Leistung des Arbeitgebers – bis 364 € für eine Familie." },
      { q: "Habe ich Anspruch auf Urlaubsgeld?", a: "Nur, wenn es im Arbeits-/Tarifvertrag oder durch betriebliche Übung zugesichert ist." },
    ],
  },

  "vermietung-versteuerung": {
    blogLinks: [{ path: "/blog/immobilien-kapitalanlage-grundlagen", label: "Immobilien als Kapitalanlage – Grundlagen" }],
    faqs: [
      { q: "Wie werden Mieteinnahmen besteuert?", a: "Mit Deinem persönlichen Einkommensteuersatz – nach Abzug aller Werbungskosten." },
      { q: "Was kann ich als Vermieter absetzen?", a: "Kreditzinsen, AfA, Verwaltungs-, Reparatur- und Renovierungskosten, Fahrtkosten zur Immobilie u. v. m." },
      { q: "Was ist die AfA?", a: "Die Absetzung für Abnutzung – für vermietete Wohnimmobilien meist 2 % der Gebäudekosten pro Jahr." },
    ],
  },

  wechselkosten: {
    blogLinks: [{ path: "/blog/private-krankenversicherung-wechsel", label: "Private Krankenversicherung – Wechsel und Kosten" }],
    faqs: [
      { q: "Was kostet ein Produktwechsel?", a: "Je nach Produkt: Stornogebühren, neue Abschlusskosten, Steuern auf realisierte Gewinne, Depotübertragungsgebühren." },
      { q: "Wann lohnt sich ein Wechsel trotzdem?", a: "Wenn die laufenden Kostenersparnis (z. B. günstigere ETFs) die Wechselkosten innerhalb von 1–3 Jahren wieder hereinholt." },
      { q: "Wie minimiere ich Wechselkosten?", a: "Depotübertragung statt Verkauf wählen, Steuerstundungseffekt nutzen, Aktionen der Broker vergleichen." },
    ],
  },

  wertpapier: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist ein Wertpapier?", a: "Ein handelbares Finanzinstrument, das einen Vermögenswert verbrieft – etwa einen Unternehmensanteil (Aktie) oder eine Forderung (Anleihe)." },
      { q: "Welche Wertpapiere gibt es?", a: "Aktien, Anleihen, Fonds, ETFs, Derivate (Optionen, Zertifikate), strukturierte Produkte u. a." },
      { q: "Wie kaufe ich Wertpapiere?", a: "Über ein Depot bei einer Bank oder einem Online-Broker – per Kauforder an einer Börse oder Handelsplattform." },
    ],
  },

  zins: {
    blogLinks: [{ path: "/blog/zinswende-altersvorsorge-2024", label: "Zinswende und Altersvorsorge" }],
    faqs: [
      { q: "Was ist ein Zins?", a: "Die Vergütung für die Bereitstellung oder Aufnahme von Kapital – meist als Prozentsatz pro Jahr angegeben." },
      { q: "Was ist der Unterschied zwischen Soll- und Habenzins?", a: "Sollzins zahlst Du für Kredite, Habenzins bekommst Du für Guthaben." },
      { q: "Wie beeinflusst der Leitzins mein Tagesgeld?", a: "Steigt der EZB-Leitzins, bieten Banken meist auch höhere Tagesgeldzinsen – mit etwas Verzögerung." },
    ],
  },

  zwangsvollstreckung: {
    faqs: [
      { q: "Was ist eine Zwangsvollstreckung?", a: "Ein gerichtliches Verfahren, mit dem Gläubiger ihre Forderungen zwangsweise durchsetzen – etwa per Lohn- oder Kontopfändung." },
      { q: "Wie schütze ich mich davor?", a: "Mit solider Finanzplanung, Notgroschen, Berufsunfähigkeits- und Haftpflichtversicherung sowie pünktlicher Zahlung von Rechnungen." },
      { q: "Was passiert bei Zahlungsunfähigkeit?", a: "Mahnverfahren, Vollstreckungstitel, ggf. Kontopfändung – bis hin zur Privatinsolvenz mit Restschuldbefreiung nach 3 Jahren." },
    ],
  },
  abfindung: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" }],
    faqs: [
      { q: "Was ist eine Abfindung?", a: "Eine freiwillige Entschädigungszahlung des Arbeitgebers für den Verlust des Arbeitsplatzes." },
      { q: "Wie wird sie besteuert?", a: "Voll lohnsteuerpflichtig, aber sozialversicherungsfrei. Mit Fünftelregelung kann die Steuerlast deutlich sinken." },
      { q: "Was ist die Fünftelregelung?", a: "Eine Steuerermäßigung, bei der die Abfindung rechnerisch auf fünf Jahre verteilt wird – das senkt den progressiven Steuersatz." },
      { q: "Wie viel Abfindung steht mir zu?", a: "Es gibt keinen gesetzlichen Anspruch. Faustregel: 0,5 Bruttomonatsgehälter pro Beschäftigungsjahr." },
    ],
  },

  afa: {
    blogLinks: [{ path: "/blog/immobilien-kapitalanlage-grundlagen", label: "Immobilien als Kapitalanlage – Grundlagen" }],
    faqs: [
      { q: "Was ist die AfA?", a: "Eine steuerliche Abschreibung, mit der Vermieter die Kosten einer Immobilie über die Nutzungsdauer absetzen." },
      { q: "Wie berechne ich die Abschreibung?", a: "Gebäudewert × AfA-Satz (2 % oder 3 %). Beispiel: 300.000 € Gebäudewert × 2 % = 6.000 € jährlich absetzbar." },
      { q: "Wer profitiert von der AfA?", a: "Alle, die eine Immobilie vermieten – Privatpersonen wie Investoren. Eigennutzer können die AfA nicht nutzen." },
    ],
  },

  altersarmut: {
    blogLinks: [
      { path: "/blog/altersarmut-vermeiden-vorsorge", label: "Altersarmut vermeiden – mit kluger Vorsorge" },
      { path: "/blog/altersvorsorge-frauen-rentenluecke", label: "Altersvorsorge für Frauen – die Rentenlücke schließen" },
    ],
    faqs: [
      { q: "Was ist Altersarmut?", a: "Das Leben im Alter unterhalb der Armutsgefährdungsschwelle – aktuell rund 1.250 € netto pro Monat für Singles." },
      { q: "Wie vermeide ich Altersarmut?", a: "Früh privat vorsorgen (ETF-Sparplan, bAV, Rürup), Erwerbsbiografie sichern (Berufsunfähigkeitsversicherung), Steuern und Förderungen optimal nutzen." },
      { q: "Ab wann bin ich armutsgefährdet im Alter?", a: "Wenn das verfügbare Einkommen unter 60 % des mittleren Einkommens liegt – aktuell rund 1.250 € netto monatlich für Singles." },
    ],
  },

  bausparen: {
    blogLinks: [{ path: "/blog/wohn-riester-eigenheim-altersvorsorge", label: "Wohn-Riester – Eigenheim und Altersvorsorge" }],
    faqs: [
      { q: "Was ist Bausparen?", a: "Ein Vertrag, bei dem Du erst Kapital ansparst und danach ein zinsgünstiges Bauspardarlehen bekommst." },
      { q: "Lohnt sich ein Bausparvertrag noch?", a: "In Hochzinsphasen weniger, in Niedrigzinsphasen als Zinssicherung für die Zukunft. ETF-Sparpläne bieten oft mehr Rendite." },
      { q: "Was ist der Unterschied zwischen Bausparvertrag und Hypothek?", a: "Ein Bausparvertrag kombiniert Sparen und Darlehen mit festen Zinsen; eine Hypothek ist nur ein klassischer Immobilienkredit." },
    ],
  },

  berufsunfaehigkeitsrente: {
    blogLinks: [
      { path: "/blog/berufsunfaehigkeitsversicherung-sinnvoll", label: "Berufsunfähigkeitsversicherung – sinnvoll und wichtig" },
      { path: "/blog/arbeitskraft-absichern-versicherungen", label: "Arbeitskraft absichern – die wichtigsten Versicherungen" },
    ],
    faqs: [
      { q: "Wie hoch sollte meine Berufsunfähigkeitsrente sein?", a: "60–80 % Deines aktuellen Nettoeinkommens – als Faustregel mindestens 1.500 € monatlich." },
      { q: "Ab wann zahlt die Berufsunfähigkeitsversicherung?", a: "Sobald Du nachweislich zu mindestens 50 % berufsunfähig bist, voraussichtlich für mindestens 6 Monate." },
      { q: "Was ist der Unterschied zwischen Berufsunfähigkeitsversicherung und EM-Rente?", a: "Die Berufsunfähigkeitsrente schützt den zuletzt ausgeübten Beruf; die EM-Rente nur, wenn Du gar nicht mehr arbeiten kannst – und sie ist deutlich niedriger." },
    ],
  },

  dax: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist der DAX?", a: "Der wichtigste deutsche Aktienindex mit den 40 größten börsennotierten Unternehmen Deutschlands." },
      { q: "Wie investiere ich in den DAX?", a: "Über einen ETF, der den DAX 1:1 nachbildet – z. B. iShares Core DAX oder Xtrackers DAX." },
      { q: "Ist der DAX sicherer als der MSCI World?", a: "Nein. Der DAX konzentriert sich auf 40 deutsche Aktien, der MSCI World streut über rund 1.500 Aktien weltweit – das ist deutlich sicherer." },
    ],
  },

  deflation: {
    blogLinks: [{ path: "/blog/inflation-erspartes-schuetzen-2024", label: "Inflation – wie Du Dein Erspartes schützt" }],
    faqs: [
      { q: "Was ist Deflation?", a: "Ein anhaltender Rückgang des allgemeinen Preisniveaus – die Kaufkraft des Geldes steigt, die Wirtschaftsleistung sinkt meist." },
      { q: "Warum ist Deflation gefährlich?", a: "Weil Konsum und Investitionen einbrechen – das führt zu Arbeitslosigkeit, Insolvenzen und einer wirtschaftlichen Abwärtsspirale." },
      { q: "Wie schütze ich mein Geld bei Deflation?", a: "Bargeld und Anleihen bester Bonität gewinnen an Wert; breit gestreute Aktien-ETFs bleiben langfristig sinnvoll." },
    ],
  },

  dispositionskredit: {
    blogLinks: [{ path: "/blog/geldanlage-einsteiger-guide", label: "Geldanlage für Einsteiger – der komplette Guide" }],
    faqs: [
      { q: "Was ist ein Dispositionskredit?", a: "Eine von der Bank eingeräumte Überziehungsmöglichkeit auf dem Girokonto." },
      { q: "Wie teuer ist der Dispo?", a: "10–15 % p. a. – deutlich teurer als ein klassischer Ratenkredit (4–8 %)." },
      { q: "Wie komme ich aus dem Dispo raus?", a: "Den Dispo per günstigem Ratenkredit umschulden, Ausgaben senken, Notgroschen aufbauen." },
    ],
  },

  erbschaftssteuer: {
    blogLinks: [{ path: "/blog/vermoegen-vererben-schenkung-erbschaft", label: "Vermögen vererben – Schenkung und Erbschaft klug regeln" }],
    faqs: [
      { q: "Wie hoch ist die Erbschaftssteuer?", a: "Je nach Steuerklasse und Vermögenshöhe zwischen 7 % und 50 %." },
      { q: "Wie spare ich Erbschaftssteuer?", a: "Schenkungen alle 10 Jahre, Nutzung der Freibeträge, Testament oder – bei größeren Vermögen – Familiengesellschaften." },
      { q: "Was sind die Freibeträge?", a: "Ehepartner 500.000 €, Kinder 400.000 € pro Elternteil, Enkel 200.000 €, Geschwister 20.000 € – jeweils alle 10 Jahre." },
    ],
  },

  erholungsbeihilfe: {
    blogLinks: [{ path: "/blog/urlaubsgeld-2026-steuern-erholungsbeihilfe", label: "Urlaubsgeld 2026 – Steuern & Erholungsbeihilfe" }],
    faqs: [
      { q: "Was ist die Erholungsbeihilfe?", a: "Eine pauschal versteuerte Zahlung des Arbeitgebers für Erholungszwecke." },
      { q: "Wie viel ist steuerfrei?", a: "156 € für Dich, 104 € für Deinen Partner, 52 € pro Kind – bis zu 364 € pro Jahr für eine Familie." },
      { q: "Was ist der Unterschied zu Urlaubsgeld?", a: "Urlaubsgeld ist voll steuer- und abgabenpflichtig; die Erholungsbeihilfe wird pauschal vom Arbeitgeber versteuert und ist für Dich abgabenfrei." },
    ],
  },

  finanzplan: {
    blogLinks: [
      { path: "/blog/berufseinsteiger-finanzen-schritte", label: "Berufseinsteiger – die ersten Finanzschritte" },
      { path: "/blog/finanzielle-vorsaetze-2025-plan", label: "Finanzielle Vorsätze – der 2025-Plan" },
    ],
    faqs: [
      { q: "Was ist ein Finanzplan?", a: "Eine systematische Übersicht über Deine Finanzen – Einnahmen, Ausgaben, Vermögen, Schulden und Ziele." },
      { q: "Wie erstelle ich einen Finanzplan?", a: "Bestandsaufnahme machen, Ziele definieren, Strategie ableiten – idealerweise mit einem unabhängigen Finanzberater." },
      { q: "Was gehört in einen Finanzplan?", a: "Einkommensanalyse, Vermögensaufstellung, Schulden, Vorsorge, Versicherungen, kurz-/mittel-/langfristige Ziele." },
    ],
  },

  fondssparplan: {
    blogLinks: [
      { path: "/blog/sparplan-vs-einmalanlage", label: "Sparplan vs. Einmalanlage – was ist besser?" },
      { path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" },
    ],
    faqs: [
      { q: "Was ist ein Fondssparplan?", a: "Ein automatischer monatlicher Kauf von Fonds- oder ETF-Anteilen." },
      { q: "Ab wann lohnt er sich?", a: "Schon ab 25 € monatlich – je länger der Anlagehorizont, desto stärker wirkt der Zinseszinseffekt." },
      { q: "Fondssparplan oder ETF-Sparplan?", a: "ETF-Sparpläne sind in der Regel günstiger (TER 0,1–0,3 %) als klassische aktive Fondssparpläne (TER 1,5–2 %)." },
    ],
  },

  gehaltszettel: {
    blogLinks: [{ path: "/blog/steuerklasse-optimieren-lohnsteuer", label: "Steuerklasse optimieren und Lohnsteuer senken" }],
    faqs: [
      { q: "Was steht auf dem Gehaltszettel?", a: "Bruttolohn, Steuern, Sozialabgaben, Zuschüsse, Nettolohn sowie Jahreswerte und ggf. Sachbezüge." },
      { q: "Was bedeuten die einzelnen Positionen?", a: "LSt = Lohnsteuer, SolZ = Soli, KiSt = Kirchensteuer, KV/RV/AV/PV = Kranken-/Renten-/Arbeitslosen-/Pflegeversicherung." },
      { q: "Wie lese ich meinen Gehaltszettel richtig?", a: "Von oben nach unten: Bruttolohn → Steuern → Sozialabgaben → Nettolohn → Zuschüsse → Auszahlungsbetrag." },
    ],
  },

  geldmarkt: {
    blogLinks: [{ path: "/blog/etf-vs-tagesgeld-2024", label: "ETF vs. Tagesgeld – was lohnt sich mehr?" }],
    faqs: [
      { q: "Was ist der Geldmarkt?", a: "Der Markt für kurzfristige Geldanlagen mit Laufzeiten unter einem Jahr." },
      { q: "Was ist ein Geldmarkt-ETF?", a: "Ein ETF, der in kurzfristige, sehr sichere Geldmarktinstrumente investiert – z. B. orientiert am €STR." },
      { q: "Ist ein Geldmarkt-ETF sicher?", a: "Sehr sicher – das Anlagerisiko ist minimal, allerdings kein Einlagensicherungsschutz wie bei Tagesgeld." },
    ],
  },

  "gesetzliche-unfallversicherung": {
    faqs: [
      { q: "Was ist die gesetzliche Unfallversicherung?", a: "Eine Pflichtversicherung für Arbeitnehmer bei Arbeits-, Wege- und Berufsunfällen." },
      { q: "Was deckt sie ab?", a: "Medizinische Behandlung, Reha, Verletztengeld und ggf. Unfallrente bei dauerhafter Minderung der Erwerbsfähigkeit." },
      { q: "Brauche ich zusätzlich eine private Unfallversicherung?", a: "In der Regel ja: Die gesetzliche Unfallversicherung greift nur bei Arbeits- und Wegeunfällen. Unfälle in Freizeit, Haushalt, Sport oder Urlaub sind nicht abgedeckt – eine private Unfallversicherung schließt diese Lücke rund um die Uhr." },
    ],
  },

  haushaltsplan: {
    blogLinks: [{ path: "/blog/berufseinsteiger-finanzen-schritte", label: "Berufseinsteiger – die ersten Finanzschritte" }],
    faqs: [
      { q: "Wie erstelle ich einen Haushaltsplan?", a: "Alle Einnahmen und Ausgaben 3 Monate erfassen, Fixkosten / variable Kosten / Sparrate aufschlüsseln." },
      { q: "Was gehört rein?", a: "Einnahmen (Lohn, Nebeneinkünfte), Fixkosten (Miete, Versicherungen), variable Kosten, Sparen und Investieren." },
      { q: "Wie viel sollte ich sparen?", a: "Mindestens 10 %, ideal 20 % des Nettoeinkommens – nach der bekannten 50-30-20-Regel." },
    ],
  },

  "immobilie-kapitalanlage": {
    blogLinks: [{ path: "/blog/immobilien-kapitalanlage-grundlagen", label: "Immobilien als Kapitalanlage – Grundlagen" }],
    faqs: [
      { q: "Lohnt sich eine Immobilie als Kapitalanlage?", a: "Nur bei guter Lage, soliden Mietern und passenden Zinsen. Mietrendite sollte deutlich über 4 % brutto liegen." },
      { q: "Was ist die Mietrendite?", a: "Das Verhältnis von Jahresnettokaltmiete zum Kaufpreis – als Brutto- oder Nettomietrendite berechnet." },
      { q: "Immobilie oder ETF – was ist besser?", a: "ETFs sind flexibler, kostengünstiger und ohne Klumpenrisiko. Immobilien bieten Hebel durch Fremdkapital und Inflationsschutz." },
    ],
  },

  insolvenz: {
    blogLinks: [{ path: "/blog/arbeitgeber-insolvenz-arbeitnehmer-rechte-absicherung", label: "Arbeitgeber-Insolvenz – Rechte und Absicherung für Arbeitnehmer" }],
    faqs: [
      { q: "Was ist Privatinsolvenz?", a: "Ein gerichtliches Verfahren für überschuldete Privatpersonen mit dem Ziel der Restschuldbefreiung." },
      { q: "Wie lange dauert das Verfahren?", a: "Seit Oktober 2020: 3 Jahre Wohlverhaltensphase – danach werden die Restschulden erlassen." },
      { q: "Was passiert mit meinen Schulden?", a: "Pfändbares Einkommen geht an den Treuhänder, der es an die Gläubiger verteilt; Restschulden werden nach 3 Jahren erlassen." },
    ],
  },

  "jahresabschluss-persoenlich": {
    blogLinks: [{ path: "/blog/finanzielle-vorsaetze-2025-plan", label: "Finanzielle Vorsätze – der 2025-Plan" }],
    faqs: [
      { q: "Warum brauche ich einen persönlichen Jahresabschluss?", a: "Um den Überblick zu behalten, Ziele zu prüfen und frühzeitig nachsteuern zu können." },
      { q: "Was prüfe ich dabei?", a: "Vermögensentwicklung, Sparquote, Versicherungsbedarf, Vorsorgestand, Steueroptimierung, neue Ziele." },
      { q: "Wie oft sollte ich meine Finanzen überprüfen?", a: "Mindestens einmal jährlich, größere Lebensereignisse (Job, Familie) immer als Anlass für ein Update nehmen." },
    ],
  },

  kreditkarte: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Kreditkarte oder EC-Karte?", a: "Im Ausland und online ist die Kreditkarte oft Pflicht. Im Alltag reicht die Debitkarte (EC-Karte)." },
      { q: "Was kostet eine Kreditkarte?", a: "Von kostenlos (gute Direktbanken) bis 100 € jährlich (Premium-Karten). Achte auf Fremdwährungs- und Bargeldgebühren." },
      { q: "Worauf achte ich beim Kreditkartenvergleich?", a: "Jahresgebühr, Fremdwährungsentgelt, Bargeldabhebungen, Versicherungsleistungen, Cashback und Rückzahlungsmodus." },
    ],
  },

  laufzeit: {
    blogLinks: [{ path: "/blog/sparplan-vs-einmalanlage", label: "Sparplan vs. Einmalanlage – was ist besser?" }],
    faqs: [
      { q: "Was ist die Laufzeit bei einem Sparplan?", a: "ETF-Sparpläne haben meist keine feste Laufzeit – Du kannst jederzeit pausieren, anpassen oder verkaufen." },
      { q: "Wie lange sollte ich investieren?", a: "Für Aktien-ETFs mindestens 10–15 Jahre, idealerweise 20+ Jahre, um Schwankungen auszugleichen." },
      { q: "Was passiert, wenn ich vor Laufzeitende kündige?", a: "Bei ETF-Sparplänen: kein Problem. Bei Festgeld: oft Zinsabschlag. Bei Lebensversicherungen: meist hohe Verluste durch Stornogebühren." },
    ],
  },

  leitzins: {
    blogLinks: [{ path: "/blog/zinswende-altersvorsorge-2024", label: "Zinswende und Altersvorsorge" }],
    faqs: [
      { q: "Was ist der Leitzins?", a: "Der Zinssatz, zu dem sich Geschäftsbanken Geld bei der Zentralbank leihen können." },
      { q: "Wie beeinflusst er mein Tagesgeld?", a: "Steigt der Leitzins, steigen meist auch die Tagesgeldzinsen – allerdings mit Verzögerung und nicht 1:1." },
      { q: "Was macht die EZB mit dem Leitzins?", a: "Sie steuert damit die Inflation: Steigender Leitzins bremst die Wirtschaft, sinkender stimuliert sie." },
    ],
  },

  mietrendite: {
    blogLinks: [{ path: "/blog/immobilien-kapitalanlage-grundlagen", label: "Immobilien als Kapitalanlage – Grundlagen" }],
    faqs: [
      { q: "Was ist die Mietrendite?", a: "Der prozentuale jährliche Ertrag aus einer vermieteten Immobilie im Verhältnis zum Kaufpreis." },
      { q: "Wie berechne ich sie?", a: "Bruttomietrendite = Jahresnettokaltmiete ÷ Kaufpreis × 100. Nettomietrendite zieht zusätzlich Kosten ab." },
      { q: "Was ist eine gute Mietrendite?", a: "Brutto mindestens 4–5 %, netto deutlich über 3 %. In Top-Lagen oft niedriger, dafür Wertsteigerung höher." },
    ],
  },

  nettokaltmiete: {
    blogLinks: [{ path: "/blog/immobilien-kapitalanlage-grundlagen", label: "Immobilien als Kapitalanlage – Grundlagen" }],
    faqs: [
      { q: "Was ist die Nettokaltmiete?", a: "Die reine Miete für die Nutzung der Wohnung – ohne Betriebs- und Heizkosten." },
      { q: "Wie oft darf die Miete erhöht werden?", a: "Maximal alle 15 Monate bis zur ortsüblichen Vergleichsmiete, höchstens 20 % in 3 Jahren (in Ballungsgebieten 15 %)." },
      { q: "Was ist der Unterschied zur Warmmiete?", a: "Die Warmmiete enthält zusätzlich Betriebskosten und Heizkosten – das, was Du monatlich tatsächlich überweist." },
    ],
  },

  "online-banking": {
    faqs: [
      { q: "Was ist Online-Banking?", a: "Die digitale Abwicklung von Bankgeschäften über Internetbrowser oder Smartphone-App." },
      { q: "Ist Online-Banking sicher?", a: "Ja, sehr sicher – wenn Du Zwei-Faktor-Authentifizierung nutzt, sichere Passwörter wählst und auf Phishing-Mails achtest." },
      { q: "Welche Direktbank ist am besten?", a: "ING, DKB und Comdirect gehören seit Jahren zu den Top-Anbietern – mit kostenfreien Konten und gutem Service." },
    ],
  },

  "passives-einkommen": {
    blogLinks: [{ path: "/blog/dividendenstrategie-passives-einkommen", label: "Dividendenstrategie für passives Einkommen" }],
    faqs: [
      { q: "Was ist passives Einkommen?", a: "Einkommen, das ohne aktive Arbeit fließt – zum Beispiel aus Investments, Vermietung oder Lizenzen." },
      { q: "Wie baue ich passives Einkommen auf?", a: "Über Dividenden-ETFs oder -Aktien, vermietete Immobilien, Zinsanlagen, Online-Geschäfte oder Lizenzeinnahmen." },
      { q: "Wie viel passives Einkommen brauche ich für finanzielle Freiheit?", a: "Mindestens so viel, dass Deine monatlichen Ausgaben gedeckt sind – als Kapitalbasis das 25-Fache Deiner Jahresausgaben." },
    ],
  },

  pensionskasse: {
    blogLinks: [{ path: "/blog/betriebliche-altersvorsorge-steuerfrei", label: "Betriebliche Altersvorsorge steuerfrei nutzen" }],
    faqs: [
      { q: "Was ist eine Pensionskasse?", a: "Eine eigenständige Versorgungseinrichtung, die betriebliche Altersvorsorge für einen oder mehrere Arbeitgeber anbietet." },
      { q: "Was ist der Unterschied zur Direktversicherung?", a: "Die Direktversicherung ist eine klassische Lebens-/Rentenversicherung; die Pensionskasse ist eine eigenständige Einrichtung mit oft kollektiver Kapitalanlage." },
      { q: "Was passiert mit meiner Pensionskasse beim Jobwechsel?", a: "Du kannst sie mitnehmen, beitragsfrei stellen oder privat weiterzahlen – sofern der neue Arbeitgeber dies unterstützt." },
    ],
  },

  risikostreuung: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist Risikostreuung?", a: "Die Verteilung von Kapital auf viele unterschiedliche Anlagen, um Klumpenrisiken zu vermeiden." },
      { q: "Wie streue ich richtig?", a: "Über Anlageklassen (Aktien, Anleihen), Regionen (Welt, Schwellenländer), Branchen und Einzeltitel – am einfachsten per breitem ETF." },
      { q: "Wie viele ETFs brauche ich für gute Streuung?", a: "Schon ein einziger weltweit gestreuter ETF reicht in den meisten Fällen aus." },
    ],
  },

  sachbezug: {
    blogLinks: [{ path: "/blog/urlaubsgeld-2026-steuern-erholungsbeihilfe", label: "Urlaubsgeld 2026 – Steuern & Erholungsbeihilfe" }],
    faqs: [
      { q: "Was ist ein Sachbezug?", a: "Eine geldwerte, aber nicht-monetäre Leistung des Arbeitgebers an den Arbeitnehmer." },
      { q: "Was ist steuer- und abgabenfrei?", a: "Sachbezüge bis 50 € monatlich, Aufmerksamkeiten bis 60 € zu persönlichen Anlässen und die Erholungsbeihilfe pauschal." },
      { q: "Wie nutze ich Sachbezüge optimal?", a: "Sprich mit Deinem Arbeitgeber: Tank-, Restaurant- oder Einkaufsgutscheine bis 50 € monatlich erhöhen Dein Netto spürbar." },
    ],
  },

  selbstbehalt: {
    blogLinks: [{ path: "/blog/private-krankenversicherung-wechsel", label: "Private Krankenversicherung – Wechsel und Kosten" }],
    faqs: [
      { q: "Was ist der Selbstbehalt?", a: "Der Eigenanteil, den Du im Schadensfall selbst zahlst, bevor die Versicherung greift." },
      { q: "Wann lohnt sich ein hoher Selbstbehalt?", a: "Wenn Du Kleinschäden problemlos aus der Portokasse zahlen kannst – die Prämienersparnis ist oft erheblich." },
      { q: "Wie beeinflusst er die Prämie?", a: "Je höher der Selbstbehalt, desto niedriger die Prämie. Bei der PKV kann das bis zu 30 % Ersparnis bedeuten." },
    ],
  },

  sondertilgung: {
    faqs: [
      { q: "Was ist eine Sondertilgung?", a: "Eine zusätzliche Rückzahlung auf einen Kredit – außerhalb der vereinbarten regulären Tilgungsrate." },
      { q: "Wie viel spare ich dadurch?", a: "Je nach Zinsniveau und Restschuld oft 10.000–30.000 € Zinsen über die Gesamtlaufzeit eines Immobilienkredits." },
      { q: "Wann ist eine Sondertilgung sinnvoll?", a: "Bei hohen Kreditzinsen, sicherem Einkommen und fehlenden besseren Anlagealternativen (z. B. ETF-Rendite klar unter dem Sollzins)." },
    ],
  },

  tarifvertrag: {
    faqs: [
      { q: "Was ist ein Tarifvertrag?", a: "Ein Vertrag zwischen Gewerkschaft und Arbeitgeberverband, der Arbeitsbedingungen und Gehälter regelt." },
      { q: "Gelten für mich Tarifverträge?", a: "Ja, wenn Du in einem tarifgebundenen Unternehmen arbeitest. Auch ohne Mitgliedschaft profitierst Du meist von den Konditionen." },
      { q: "Was ist der Unterschied zu einem Haustarifvertrag?", a: "Ein Haustarifvertrag gilt nur für ein einzelnes Unternehmen, ein Flächentarifvertrag für eine ganze Branche oder Region." },
    ],
  },

  umlageverfahren: {
    blogLinks: [{ path: "/blog/generationenvertrag-rente-eigenvorsorge", label: "Generationenvertrag – warum Eigenvorsorge unverzichtbar ist" }],
    faqs: [
      { q: "Was ist das Umlageverfahren?", a: "Ein Finanzierungsmodell, bei dem die laufenden Beiträge direkt für die laufenden Rentenzahlungen verwendet werden." },
      { q: "Warum ist es problematisch?", a: "Weil immer weniger Beitragszahler immer mehr Rentner finanzieren müssen – die Beiträge steigen, das Rentenniveau sinkt." },
      { q: "Was ist der Unterschied zu Kapitaldeckung?", a: "Beim Kapitaldeckungsverfahren bildet jeder ein eigenes Kapital, das im Alter ausgezahlt wird – unabhängig von der Demografie." },
    ],
  },

  verlustverrechnung: {
    blogLinks: [{ path: "/blog/steueroptimierte-geldanlage-depot-versicherung", label: "Steueroptimierte Geldanlage: Depot vs. Versicherung" }],
    faqs: [
      { q: "Was ist die Verlustverrechnung?", a: "Die steuerliche Verrechnung von Verlusten aus Kapitalanlagen mit entsprechenden Gewinnen." },
      { q: "Wie funktioniert der Verlusttopf?", a: "Banken führen für Dich Verlusttöpfe (Aktien und Sonstige); realisierte Verluste werden automatisch mit Gewinnen verrechnet." },
      { q: "Kann ich Verluste unbegrenzt vortragen?", a: "Ja, nicht ausgeglichene Verluste werden unbegrenzt in Folgejahre vorgetragen – per Verlustbescheinigung oder automatisch." },
    ],
  },

  vollmacht: {
    faqs: [
      { q: "Was ist eine Vorsorgevollmacht?", a: "Eine schriftliche Vollmacht, mit der Du eine Vertrauensperson für den Fall Deiner Handlungsunfähigkeit bevollmächtigst." },
      { q: "Wer sollte eine haben?", a: "Jeder Erwachsene – Krankheit oder Unfall können jeden treffen, auch junge Menschen." },
      { q: "Was ist der Unterschied zum Betreuer?", a: "Ohne Vollmacht bestimmt ein Gericht einen Betreuer – mit Vollmacht entscheidet die Person Deines Vertrauens." },
    ],
  },

  wertentwicklung: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist die Wertentwicklung?", a: "Die historische Performance eines Investments über einen bestimmten Zeitraum – meist 1, 3, 5 und 10 Jahre." },
      { q: "Wie lese ich einen Fondschart?", a: "Achte auf die Y-Achse (Skalierung), Zeiträume, Vergleich mit der Benchmark und die maximalen Drawdowns." },
      { q: "Was ist eine reale vs. nominale Rendite?", a: "Nominal = vor Inflation, real = nach Abzug der Inflation. Erst die reale Rendite zeigt den tatsächlichen Kaufkraftgewinn." },
    ],
  },

  wohnriester: {
    blogLinks: [{ path: "/blog/wohn-riester-eigenheim-altersvorsorge", label: "Wohn-Riester – Eigenheim und Altersvorsorge" }],
    faqs: [
      { q: "Was ist Wohnriester?", a: "Eine Variante der Riester-Rente, bei der die Förderung in selbstgenutztes Wohneigentum fließt (§ 92a EStG)." },
      { q: "Wie funktioniert er?", a: "Riester-Guthaben darf in die Immobilie gesteckt werden; das geförderte Kapital wird auf einem Wohnförderkonto erfasst, jährlich mit 2 % fiktiv verzinst und im Alter nachgelagert besteuert — wahlweise verteilt bis zum 85. Lebensjahr oder als Einmalbesteuerung mit 30 % Rabatt." },
      { q: "Kann ich 2027 noch einen neuen Wohn-Riester-Vertrag abschließen?", a: "Nein. Nach dem Altersvorsorge-Reformgesetz (BGBl. I 2026 Nr. 156) sind Neuabschlüsse nur noch bis zum 31. Dezember 2026 möglich. Bestandsverträge behalten Bestandsschutz. Das ab 2027 startende Altersvorsorgedepot ist nicht für Immobilienentnahmen vorgesehen." },
      { q: "Für wen lohnt sich Wohnriester?", a: "Vor allem für Familien mit Kindern (hohe Zulagen) und Käufer mit langer Restlaufzeit bis zum Renteneintritt und Selbstnutzungsabsicht." },
    ],
  },

  zulagenantrag: {
    blogLinks: [
      { path: "/blog/riester-letzte-chance-2026", label: "Riester letzte Chance 2026 – jetzt noch abschließen?" },
      { path: "/blog/altersvorsorgedepot-2027-riester-nachfolger", label: "Altersvorsorgedepot – der Riester-Nachfolger" },
    ],
    faqs: [
      { q: "Was ist der Zulagenantrag?", a: "Der Antrag, mit dem Du Dir die staatliche Riester-Förderung sicherst." },
      { q: "Muss ich ihn selbst stellen?", a: "Nein, in der Regel reicht eine einmalige Ermächtigung an den Anbieter (Dauerzulagenantrag) – er erledigt den Rest." },
      { q: "Was passiert, wenn ich ihn vergesse?", a: "Wird der Antrag nicht innerhalb von 2 Jahren nach dem Beitragsjahr gestellt, verfallen die Zulagen unwiderruflich." },
    ],
  },
  auszahlungsplan: {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Was ist ein Auszahlungsplan?", a: "Eine strukturierte, regelmäßige Entnahme aus Deinem Vermögen – meist monatlich – zur Finanzierung des Lebensunterhalts im Ruhestand." },
      { q: "Wie lange reicht mein Kapital?", a: "Das hängt von Startsumme, Entnahmehöhe, Rendite und Inflation ab. Mit der 4-%-Regel reicht das Kapital historisch meist mindestens 30 Jahre." },
      { q: "Was ist die 4-%-Regel für Entnahmen?", a: "Eine Faustregel, die besagt: Jährlich 4 % des Startvermögens entnehmen reicht historisch über 30 Jahre, ohne das Kapital aufzubrauchen." },
    ],
  },

  "aktiv-vs-passiv": {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist der Unterschied zwischen aktiv und passiv?", a: "Aktive Fonds wählen gezielt Aktien aus, passive ETFs bilden einen Index 1:1 ab – günstiger und transparenter." },
      { q: "Lohnen sich aktive Fonds?", a: "Nur selten. Über 10+ Jahre schaffen weniger als 20 % der aktiven Fonds nach Kosten eine bessere Rendite als ihr Index." },
      { q: "Warum sind ETFs günstiger?", a: "Weil kein teures Fondsmanagement bezahlt werden muss – TER von 0,1–0,3 % statt 1,5–2 % bei aktiven Fonds." },
    ],
  },

  betriebsrente: {
    blogLinks: [{ path: "/blog/betriebliche-altersvorsorge-steuerfrei", label: "Betriebliche Altersvorsorge steuerfrei nutzen" }],
    faqs: [
      { q: "Was ist die Betriebsrente?", a: "Die Auszahlung aus einer betrieblichen Altersvorsorge im Ruhestand – als monatliche Rente oder Kapitalauszahlung." },
      { q: "Wie wird sie besteuert?", a: "Voll mit Deinem persönlichen Steuersatz im Alter (nachgelagerte Besteuerung) plus KV- und PV-Beiträge." },
      { q: "Ab wann bekomme ich sie?", a: "Frühestens ab dem 62. Lebensjahr (Verträge ab 2012) bzw. ab dem 60. Lebensjahr (Verträge davor)." },
    ],
  },

  boerse: {
    blogLinks: [{ path: "/blog/vermoegensaufbau-etf", label: "Vermögensaufbau mit ETFs – so geht's" }],
    faqs: [
      { q: "Was ist die Börse?", a: "Ein organisierter Marktplatz für den Handel mit Wertpapieren wie Aktien, ETFs und Anleihen." },
      { q: "Wie funktioniert der Börsenhandel?", a: "Käufer und Verkäufer geben Orders über einen Broker auf; das Handelssystem matched passende Angebote zu einem Kurs." },
      { q: "Brauche ich ein Depot, um an der Börse zu handeln?", a: "Ja, ohne Wertpapierdepot bei einer Bank oder einem Online-Broker ist kein Wertpapierhandel möglich." },
    ],
  },

  budgetplan: {
    blogLinks: [{ path: "/blog/berufseinsteiger-finanzen-schritte", label: "Berufseinsteiger – die ersten Finanzschritte" }],
    faqs: [
      { q: "Was ist die 50-30-20-Regel?", a: "Eine Budgetregel: 50 % für Fixkosten, 30 % für persönliche Wünsche, 20 % zum Sparen und Investieren." },
      { q: "Wie erstelle ich einen Budgetplan?", a: "Alle Einnahmen und Ausgaben 3 Monate erfassen, in Kategorien sortieren und Sparziele definieren." },
      { q: "Wie viel sollte ich monatlich sparen?", a: "Mindestens 10 %, ideal 20 % des Nettoeinkommens – je früher Du anfängst, desto stärker der Zinseszinseffekt." },
    ],
  },

  cashflow: {
    faqs: [
      { q: "Was ist Cashflow?", a: "Der tatsächliche monatliche Geldfluss – Einnahmen minus Ausgaben." },
      { q: "Wie verbessere ich meinen Cashflow?", a: "Einnahmen erhöhen (Gehaltsverhandlung, Nebeneinkünfte), Ausgaben senken (Versicherungen, Strom, Abos prüfen)." },
      { q: "Was ist der Unterschied zwischen Cashflow und Gewinn?", a: "Cashflow zählt nur reale Zahlungsströme, Gewinn berücksichtigt auch Abschreibungen, Rückstellungen und Buchwerte." },
    ],
  },

  depotverwaltung: {
    blogLinks: [{ path: "/blog/depot-check-portfolio-kurs-2026", label: "Depot-Check 2026 – ist Dein Portfolio auf Kurs?" }],
    faqs: [
      { q: "Was gehört zur Depotverwaltung?", a: "Rebalancing, Kostenkontrolle, Steueroptimierung (z. B. Freistellungsauftrag), Anpassung an neue Lebensziele." },
      { q: "Wie oft sollte ich mein Depot überprüfen?", a: "Einmal jährlich reicht in der Regel – häufigeres Reagieren führt oft zu schlechteren Renditen." },
      { q: "Was ist ein Depotumzug?", a: "Die Übertragung aller Wertpapiere zu einer anderen Bank oder einem anderen Broker – steuerneutral und meist kostenfrei." },
    ],
  },

  durchschnittskosteneffekt: {
    blogLinks: [{ path: "/blog/sparplan-vs-einmalanlage", label: "Sparplan vs. Einmalanlage – was ist besser?" }],
    faqs: [
      { q: "Was ist der Durchschnittskosteneffekt?", a: "Der Effekt, dass bei regelmäßigem Investieren mit gleichem Betrag automatisch günstiger gekauft wird." },
      { q: "Warum ist er beim Sparplan vorteilhaft?", a: "Er reduziert das Timing-Risiko und macht es psychologisch einfacher, in Krisen weiter zu investieren." },
      { q: "Kann man ihn auch bei Einmalanlagen nutzen?", a: "Indirekt – indem man eine größere Summe auf 6–12 Monate streckt, statt sie auf einmal anzulegen." },
    ],
  },

  einkommensteuer: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" }],
    faqs: [
      { q: "Was ist die Einkommensteuer?", a: "Die Steuer auf das Einkommen aus allen sieben Einkunftsarten – Lohn, Selbstständigkeit, Kapital, Vermietung u. a." },
      { q: "Wie hoch ist mein Steuersatz?", a: "Zwischen 0 % (bis 12.348 €, Grundfreibetrag 2026) und 45 % (Reichensteuersatz ab 277.826 €). Dazwischen progressiv steigend." },
      { q: "Wie kann ich Einkommensteuer sparen?", a: "Werbungskosten, Sonderausgaben, Vorsorgeaufwendungen, Rürup-Rente, Spenden und außergewöhnliche Belastungen geltend machen." },
    ],
  },

  elterngeld: {
    blogLinks: [{ path: "/blog/steuerklasse-optimieren-lohnsteuer", label: "Steuerklasse optimieren und Lohnsteuer senken" }],
    faqs: [
      { q: "Was ist Elterngeld?", a: "Eine staatliche Lohnersatzleistung nach der Geburt eines Kindes." },
      { q: "Wie hoch ist es?", a: "65–67 % des wegfallenden Nettoeinkommens, mindestens 300 €, maximal 1.800 € monatlich." },
      { q: "Was ist der Unterschied zwischen Basis- und ElterngeldPlus?", a: "BasisElterngeld bis 14 Monate in voller Höhe; ElterngeldPlus bis 24 Monate in halber Höhe – ideal in Teilzeit." },
    ],
  },

  "finanzielle-freiheit": {
    blogLinks: [{ path: "/blog/finanzielle-unabhaengigkeit-grundlagen", label: "Finanzielle Unabhängigkeit – die Grundlagen" }, { path: "/blog/vermoegensaufbau-mit-25", label: "Vermögensaufbau mit 25 – so startest Du clever" }],
    faqs: [
      { q: "Was ist finanzielle Freiheit?", a: "Der Zustand, in dem passive Einnahmen Deine Ausgaben dauerhaft decken – Erwerbsarbeit wird optional." },
      { q: "Wie viel Kapital brauche ich?", a: "Das 25-Fache Deiner jährlichen Ausgaben (4-%-Regel) – wer 36.000 € im Jahr ausgibt, braucht ca. 900.000 €." },
      { q: "Wie lange dauert es, finanzielle Freiheit zu erreichen?", a: "Je nach Sparquote: bei 50 % rund 17 Jahre, bei 30 % rund 28 Jahre, bei 10 % über 50 Jahre." },
    ],
  },

  fondsmanager: {
    blogLinks: [],
    faqs: [
      { q: "Was macht ein Fondsmanager?", a: "Er trifft die Anlageentscheidungen in einem aktiv gemanagten Fonds – Auswahl, Gewichtung, Kauf und Verkauf der Wertpapiere." },
      { q: "Lohnt sich ein aktiv gemanagter Fonds?", a: "Selten. Langfristig schaffen weniger als 20 % der Fonds eine bessere Rendite nach Kosten als ihr Vergleichsindex." },
      { q: "Wie viel kostet ein Fondsmanager?", a: "Indirekt: über die Fondskosten (TER 1,5–2 %) plus Ausgabeaufschlag (oft 5 %)." },
    ],
  },

  garantiezins: {
    blogLinks: [{ path: "/blog/fondsgebundene-rentenversicherung", label: "Fondsgebundene Rentenversicherung – Vor- und Nachteile" }],
    faqs: [
      { q: "Was ist der Garantiezins?", a: "Der gesetzlich festgelegte Höchstrechnungszins, den klassische Lebens-/Rentenversicherungen garantieren dürfen." },
      { q: "Wie hoch ist er aktuell?", a: "Seit 2025 bei 1,0 % – nach jahrelangem Rückgang ein historischer Tiefststand." },
      { q: "Lohnen sich Versicherungen mit Garantiezins noch?", a: "Kaum. Nach Kosten und Inflation bleibt real meist nichts übrig – Alternativen wie ETFs sind oft deutlich rentabler." },
    ],
  },

  gesamtschuldnerschaft: {
    blogLinks: [],
    faqs: [
      { q: "Was ist Gesamtschuldnerschaft?", a: "Mehrere Personen haften gemeinsam für eine Schuld; der Gläubiger kann jeden Einzelnen für den vollen Betrag belangen." },
      { q: "Was bedeutet das für Ehepaare?", a: "Bei gemeinsamen Krediten haftet jeder Ehepartner für die gesamte Summe – auch bei Trennung oder Scheidung." },
      { q: "Wie schütze ich mich?", a: "Im Innenverhältnis Ausgleichsvereinbarungen treffen, Risikolebensversicherung abschließen, Schulden nur gemeinsam aufnehmen, wenn nötig." },
    ],
  },

  hinterbliebenenversorgung: {
    blogLinks: [{ path: "/blog/risikolebensversicherung-wer-braucht-sie", label: "Risikolebensversicherung – wer sie wirklich braucht" }, { path: "/blog/vermoegen-vererben-schenkung-erbschaft", label: "Vermögen vererben – Schenkung und Erbschaft steuerlich klug gestalten" }],
    faqs: [
      { q: "Was ist Hinterbliebenenversorgung?", a: "Alle Maßnahmen, die Hinterbliebene im Todesfall finanziell absichern – gesetzlich, betrieblich und privat." },
      { q: "Reicht die gesetzliche Witwen- und Waisenrente?", a: "Meist nicht. Sie deckt selten den gewohnten Lebensstandard – private Absicherung über Risikoleben ist fast immer nötig." },
      { q: "Wie sichere ich meine Familie ab?", a: "Über eine Risikolebensversicherung in Höhe von 3–5 Jahresbruttogehältern – günstig und für Familien essenziell." },
    ],
  },

  hypothek: {
    blogLinks: [{ path: "/leistungen", label: "Unsere Leistungen im Überblick" }],
    faqs: [
      { q: "Was ist eine Hypothek?", a: "Ein durch eine Immobilie besichertes Darlehen zur Finanzierung von Kauf, Bau oder Modernisierung." },
      { q: "Wie bekomme ich eine Hypothek?", a: "Über Bank oder Vermittler – Voraussetzung: gesichertes Einkommen, ausreichend Eigenkapital, gute Bonität." },
      { q: "Was ist der Unterschied zwischen Hypothek und Grundschuld?", a: "Die Hypothek ist an eine konkrete Forderung gebunden; die Grundschuld nicht – sie ist flexibler und in Deutschland heute Standard." },
    ],
  },

  inflationsrate: {
    blogLinks: [{ path: "/blog/inflation-2025-vermoegen-schuetzen", label: "Inflation 2025 – Vermögen schützen" }, { path: "/blog/inflation-erspartes-schuetzen-2024", label: "Inflation – wie Du Dein Erspartes schützt" }],
    faqs: [
      { q: "Was ist die Inflationsrate?", a: "Die prozentuale Veränderung der Verbraucherpreise gegenüber dem Vorjahresmonat." },
      { q: "Wer misst sie?", a: "In Deutschland das Statistische Bundesamt anhand eines repräsentativen Warenkorbs." },
      { q: "Wie schütze ich mich vor hoher Inflation?", a: "Mit Sachwerten wie Aktien-ETFs, Immobilien und – als Beimischung – Gold oder inflationsindexierten Anleihen." },
    ],
  },

  investmentfonds: {
    blogLinks: [],
    faqs: [
      { q: "Was ist ein Investmentfonds?", a: "Ein Sondervermögen, in dem das Geld vieler Anleger gemeinsam investiert wird." },
      { q: "Wie unterscheiden sich Fonds von ETFs?", a: "ETFs sind eine spezielle Form börsengehandelter Fonds, meist passiv den Index nachbildend und deutlich günstiger als aktive Fonds." },
      { q: "Wie wähle ich den richtigen Fonds?", a: "Achte auf TER, Anlagestrategie, Volumen, Fondsdomizil und steuerliche Behandlung – für die meisten reicht ein weltweiter ETF." },
    ],
  },

  jobticket: {
    blogLinks: [],
    faqs: [
      { q: "Was ist ein Jobticket?", a: "Ein vom Arbeitgeber bezuschusstes oder vollständig finanziertes Ticket für den öffentlichen Nahverkehr." },
      { q: "Ist es steuerfrei?", a: "Ja, seit 2020 vollständig steuer- und sozialabgabenfrei – sofern es zusätzlich zum ohnehin geschuldeten Lohn gewährt wird." },
      { q: "Kann ich Jobticket und Pendlerpauschale kombinieren?", a: "Ja, aber das Jobticket wird auf die Pendlerpauschale angerechnet – die Werbungskosten reduzieren sich entsprechend." },
    ],
  },

  kapitalmarkt: {
    blogLinks: [],
    faqs: [
      { q: "Was ist der Kapitalmarkt?", a: "Der Markt für langfristige Kapitalanlagen wie Aktien, Anleihen und Fonds – mit Laufzeiten über einem Jahr." },
      { q: "Wie investiere ich am Kapitalmarkt?", a: "Am einfachsten über ein Depot und einen breit gestreuten ETF-Sparplan." },
      { q: "Ist der Kapitalmarkt riskant?", a: "Kurzfristig ja, langfristig deutlich weniger. Über 15+ Jahre haben weltweite Aktien-ETFs historisch nie Verluste gemacht." },
    ],
  },

  konsolidierung: {
    blogLinks: [],
    faqs: [
      { q: "Was ist Schuldenkonsolidierung?", a: "Die Zusammenführung mehrerer Kredite oder Verbindlichkeiten in einen einzigen, meist günstigeren Kredit." },
      { q: "Wann macht eine Umschuldung Sinn?", a: "Wenn der neue Effektivzins deutlich unter den bestehenden Krediten liegt und keine hohen Vorfälligkeitsentschädigungen anfallen." },
      { q: "Wie spare ich durch Konsolidierung?", a: "Durch niedrigere Zinsen, geringere Gesamtrate und bessere Übersicht – im Schnitt mehrere Tausend Euro über die Laufzeit." },
    ],
  },

  krankenversicherungsbeitrag: {
    blogLinks: [{ path: "/blog/beitragsbemessungsgrenze-2026", label: "Beitragsbemessungsgrenze 2026 – neue Werte" }, { path: "/blog/private-krankenversicherung-wechsel", label: "Private Krankenversicherung – Wechsel sinnvoll?" }],
    faqs: [
      { q: "Wie hoch ist der Krankenversicherungsbeitrag 2026?", a: "14,6 % Grundbeitrag plus 1,7–3,8 % Zusatzbeitrag je nach Kasse – insgesamt rund 16–18 %." },
      { q: "Was zahlt der Arbeitgeber?", a: "Die Hälfte der gesamten Beiträge – inklusive Zusatzbeitrag." },
      { q: "Wie spare ich Krankenversicherungsbeiträge?", a: "Krankenkasse mit niedrigem Zusatzbeitrag wählen oder – bei hohem Einkommen und passenden Voraussetzungen – PKV-Wechsel prüfen." },
    ],
  },

  langzeitpflege: {
    blogLinks: [{ path: "/blog/pflegeversicherung-luecke-absicherung", label: "Pflegeversicherung – die Lücke absichern" }, { path: "/blog/pflegemindestlohn-2026-kosten-absicherung", label: "Pflegemindestlohn 2026 – steigende Kosten absichern" }],
    faqs: [
      { q: "Was kostet Langzeitpflege?", a: "2026 durchschnittlich 3.000–5.000 € monatlicher Eigenanteil im Pflegeheim, je nach Pflegegrad und Bundesland." },
      { q: "Wer zahlt den Eigenanteil?", a: "Zunächst der Pflegebedürftige selbst (Rente, Vermögen). Reicht das nicht, springen Kinder ein (ab 100.000 € eigenem Bruttoeinkommen) oder die Sozialhilfe." },
      { q: "Wie plane ich Pflegekosten ein?", a: "Mit einer privaten Pflegezusatzversicherung (Pflegetagegeld) und durch frühzeitigen Vermögensaufbau für den Pflegefall." },
    ],
  },

  lohnfortzahlung: {
    blogLinks: [{ path: "/blog/krankentagegeld-luecke-absicherung", label: "Krankentagegeld – die Lücke zum Netto absichern" }],
    faqs: [
      { q: "Wie lange gilt die Lohnfortzahlung?", a: "6 Wochen pro Krankheitsfall – bei einer neuen, anderen Krankheit beginnt die Frist erneut." },
      { q: "Was passiert nach 6 Wochen?", a: "Die Krankenkasse zahlt Krankengeld (70 % brutto, max. 90 % netto) – maximal 78 Wochen wegen derselben Krankheit." },
      { q: "Was ist, wenn ich mehrfach krank bin?", a: "Bei verschiedenen Krankheiten greift die Lohnfortzahlung erneut; bei derselben gilt die 6-Wochen-Frist insgesamt." },
    ],
  },

  marktkapitalisierung: {
    blogLinks: [],
    faqs: [
      { q: "Was ist Marktkapitalisierung?", a: "Der Börsenwert eines Unternehmens – Aktienkurs × Anzahl der ausgegebenen Aktien." },
      { q: "Wie beeinflusst sie ETF-Gewichtungen?", a: "In den meisten ETFs werden Aktien nach Marktkapitalisierung gewichtet – größere Unternehmen haben einen höheren Anteil." },
      { q: "Was ist der Unterschied zwischen Large Cap und Small Cap?", a: "Large Caps haben hohe Marktkapitalisierung (>10 Mrd. €), Small Caps niedrige (<2 Mrd. €). Dazwischen liegen Mid Caps." },
    ],
  },

  mindestlohn: {
    blogLinks: [{ path: "/blog/minijob-rentenversicherung-2026", label: "Minijob und Rentenversicherung 2026" }],
    faqs: [
      { q: "Wie hoch ist der Mindestlohn 2026?", a: "Seit 1. Januar 2026 beträgt der gesetzliche Mindestlohn 13,90 € pro Stunde. Zum 1. Januar 2027 steigt er in einem zweiten Schritt auf 14,60 € (Vierte Mindestlohnanpassungsverordnung)." },
      { q: "Wer hat Anspruch darauf?", a: "Praktisch alle volljährigen Arbeitnehmer – mit wenigen Ausnahmen (z. B. Auszubildende, einzelne Praktika)." },
      { q: "Was tun bei Mindestlohnverstoß?", a: "Beim Zoll melden, anwaltlich Nachzahlung fordern – Ansprüche sind bis zu 3 Jahre rückwirkend möglich." },
    ],
  },

  nettorendite: {
    blogLinks: [],
    faqs: [
      { q: "Was ist die Nettorendite?", a: "Die Rendite nach Abzug von Steuern, Produktkosten und Inflation – das, was real übrig bleibt." },
      { q: "Wie berechne ich sie?", a: "Bruttorendite minus TER, Steuern und Inflationsrate. Online-Rechner machen es einfach." },
      { q: "Warum ist die Nettorendite wichtiger als die Bruttorendite?", a: "Weil nur die Nettorendite zeigt, wie stark Deine Kaufkraft wirklich wächst." },
    ],
  },

  opportunitaetskosten: {
    blogLinks: [],
    faqs: [
      { q: "Was sind Opportunitätskosten?", a: "Der Wert der bestmöglichen Alternative, auf die Du durch Deine Entscheidung verzichtest." },
      { q: "Welche Opportunitätskosten habe ich beim Nicht-Investieren?", a: "Die Differenz zwischen Sparbuch-/Giro-Zins und der Rendite eines breit gestreuten ETF-Portfolios – über Jahrzehnte enorm." },
      { q: "Wie berechne ich sie?", a: "Endwert in der besseren Alternative minus Endwert in der tatsächlichen Wahl – idealerweise mit Zinseszinsrechner." },
    ],
  },

  pflegegrad: {
    blogLinks: [{ path: "/blog/pflegeversicherung-luecke-absicherung", label: "Pflegeversicherung – die Lücke absichern" }],
    faqs: [
      { q: "Was sind die Pflegegrade?", a: "Eine Einstufung von 1 (geringe Beeinträchtigung) bis 5 (schwerste Beeinträchtigung mit besonderen Anforderungen)." },
      { q: "Welche Leistungen gibt es je nach Pflegegrad?", a: "Pflegegeld 332–947 €, Pflegesachleistungen 761–2.200 €, Vollstationär 770–2.005 € – je nach Pflegegrad." },
      { q: "Wie beantrage ich einen Pflegegrad?", a: "Antrag bei der eigenen Pflegekasse stellen; ein Gutachter des MD prüft anschließend die Pflegebedürftigkeit." },
    ],
  },

  privatrente: {
    blogLinks: [{ path: "/blog/fondsgebundene-rentenversicherung", label: "Fondsgebundene Rentenversicherung – Vor- und Nachteile" }, { path: "/blog/fondspolice-vs-etf-depot-vergleich", label: "Fondspolice vs. ETF-Depot – der Vergleich" }],
    faqs: [
      { q: "Was ist eine private Rentenversicherung?", a: "Ein Vertrag, bei dem Du Beiträge einzahlst und im Alter eine lebenslange Rente oder Kapitalauszahlung erhältst." },
      { q: "Lohnt sie sich?", a: "Vor allem fondsgebunden mit langem Anlagehorizont, hoher Steuerersparnis im Alter und disziplinierter Besparung." },
      { q: "Steuer- oder Versicherungsmantel – was ist besser?", a: "ETF-Depot ist flexibler, Versicherung steueroptimierter bei langer Laufzeit. Häufig ist eine Kombination ideal." },
    ],
  },

  rentenversicherungsnummer: {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Was ist die Rentenversicherungsnummer?", a: "Eine 12-stellige Nummer, mit der Du in der gesetzlichen Rentenversicherung lebenslang eindeutig identifiziert wirst." },
      { q: "Wo finde ich sie?", a: "Auf dem Sozialversicherungsausweis, der jährlichen Renteninformation und der Krankenkassenkarte." },
      { q: "Was mache ich, wenn ich sie verloren habe?", a: "Kostenlos bei der Deutschen Rentenversicherung anfordern – online, per Telefon oder schriftlich." },
    ],
  },

  steuerabzug: {
    blogLinks: [{ path: "/blog/freistellungsauftrag-richtig-nutzen", label: "Freistellungsauftrag richtig nutzen" }],
    faqs: [
      { q: "Was ist ein Steuerabzug?", a: "Eine Ausgabe, die das zu versteuernde Einkommen mindert – im Gegensatz zu einem direkten Abzug von der Steuerschuld." },
      { q: "Was kann ich alles abziehen?", a: "Werbungskosten (Arbeitsweg, Homeoffice, Fortbildung), Sonderausgaben (Spenden, Kirchensteuer), Vorsorgeaufwendungen, außergewöhnliche Belastungen." },
      { q: "Wie maximiere ich meinen Steuerabzug?", a: "Alle Belege sammeln, Pauschalen prüfen, Rürup nutzen, Steuersoftware oder Beratung einsetzen." },
    ],
  },

  steuerprogression: {
    blogLinks: [{ path: "/blog/steuerklasse-optimieren-lohnsteuer", label: "Steuerklasse optimieren – Lohnsteuer senken" }],
    faqs: [
      { q: "Was ist Steuerprogression?", a: "Das Prinzip, dass mit steigendem Einkommen auch der Steuersatz steigt." },
      { q: "Was ist der Unterschied zwischen Grenz- und Durchschnittssteuersatz?", a: "Der Grenzsteuersatz gilt für den nächsten verdienten Euro, der Durchschnittssteuersatz für das gesamte Einkommen." },
      { q: "Wie reduziere ich meinen Steuersatz?", a: "Mit Vorsorgeaufwendungen (Rürup), Werbungskosten, Splittingtarif bei Verheirateten und cleverer Verteilung von Sonderzahlungen." },
    ],
  },

  steuerstundung: {
    blogLinks: [{ path: "/blog/steuerstundung-strategie-vorteile", label: "Steuerstundung – Strategie und Vorteile" }, { path: "/blog/steueroptimierte-geldanlage-depot-versicherung", label: "Steueroptimierte Geldanlage: Depot vs. Versicherung" }],
    faqs: [
      { q: "Was ist eine Steuerstundung?", a: "Das legale Aufschieben der Steuerzahlung auf einen späteren Zeitpunkt – meist den Verkauf des Wertpapiers." },
      { q: "Wie nutze ich sie bei ETFs?", a: "Thesaurierende ETFs wählen – Erträge werden reinvestiert, Steuern fallen erst beim Verkauf an (außer Vorabpauschale)." },
      { q: "Was ist der Vorteil gegenüber ausschüttenden Fonds?", a: "Mehr Geld bleibt langfristig investiert; der Zinseszinseffekt wirkt stärker." },
    ],
  },

  "transparente-finanzberatung": {
    blogLinks: [{ path: "/blog/unabhaengige-finanzberatung-vorteile", label: "Unabhängige Finanzberatung – die Vorteile" }],
    faqs: [
      { q: "Was ist transparente Finanzberatung?", a: "Eine Beratung, bei der Vergütung, Interessenkonflikte und Empfehlungen offen kommuniziert werden." },
      { q: "Was ist der Unterschied zwischen Provisions- und Honorarberatung?", a: "Provisionsberater werden von Produktgebern bezahlt, Honorarberater direkt vom Kunden – das eliminiert Interessenkonflikte." },
      { q: "Wie erkenne ich einen unabhängigen Berater?", a: "Frag nach der Vergütungsstruktur, schau auf Zertifikate (z. B. § 34h GewO) und prüf, ob Produkte vieler Anbieter angeboten werden." },
    ],
  },

  ueberschussbeteiligung: {
    blogLinks: [],
    faqs: [
      { q: "Was ist die Überschussbeteiligung?", a: "Ein freiwilliger Gewinnanteil aus den Überschüssen des Versicherers – zusätzlich zum garantierten Zins." },
      { q: "Ist sie garantiert?", a: "Nein. Sie wird jährlich neu festgelegt und kann sinken oder ganz ausfallen." },
      { q: "Wie hoch ist sie typischerweise?", a: "Bei klassischen Verträgen aktuell rund 1–2 % zusätzlich zum Garantiezins – Tendenz fallend." },
    ],
  },

  vermoegensteuer: {
    blogLinks: [],
    faqs: [
      { q: "Gibt es in Deutschland eine Vermögensteuer?", a: "Aktuell nein – sie ist seit 1997 ausgesetzt, wird aber politisch immer wieder diskutiert." },
      { q: "Was ist die Vermögensabgabe?", a: "Eine einmalige Sonderabgabe auf großes Vermögen, die in Krisen erwogen wird – derzeit nicht erhoben." },
      { q: "Wie schütze ich mich vor einer möglichen Vermögensteuer?", a: "Mit langfristiger Planung, internationaler Diversifikation und steueroptimierten Strukturen – idealerweise mit professioneller Beratung." },
    ],
  },

  vorsorgeaufwendungen: {
    blogLinks: [{ path: "/blog/steuern-sparen-angestellter-tipps", label: "Steuern sparen als Angestellter – Top-Tipps" }],
    faqs: [
      { q: "Was sind Vorsorgeaufwendungen?", a: "Beiträge zu bestimmten Versicherungen und Altersvorsorgeverträgen, die steuerlich begünstigt sind." },
      { q: "Wie viel kann ich absetzen?", a: "Altersvorsorge bis 30.826 € (Single, 2026), sonstige Vorsorge bis 1.900 € (Arbeitnehmer) bzw. 2.800 € (Selbstständige)." },
      { q: "Was ist der Höchstbetrag?", a: "Für Altersvorsorgeaufwendungen 2026: 30.826 € (Single) bzw. 61.652 € (Verheiratete) – jährlich steigend." },
    ],
  },

  "wartezeit-rente": {
    blogLinks: [{ path: "/blog/rentenluecke-berechnen-schliessen", label: "Rentenlücke berechnen und schließen" }],
    faqs: [
      { q: "Was ist die Wartezeit bei der Rente?", a: "Die Mindestversicherungszeit, die für einen Rentenanspruch nötig ist – je nach Rentenart 5 bis 45 Jahre." },
      { q: "Wie lange muss ich eingezahlt haben?", a: "Mindestens 5 Jahre für die Regelaltersrente, 35 Jahre für die Altersrente für langjährig Versicherte, 45 Jahre für abschlagsfrei mit 63/65." },
      { q: "Zählen auch Kindererziehungszeiten?", a: "Ja – Kindererziehungs-, Pflege-, Krankheits- und Arbeitslosigkeitszeiten werden auf die Wartezeit angerechnet." },
    ],
  },

  wkn: {
    blogLinks: [],
    faqs: [
      { q: "Was ist die WKN?", a: "Eine 6-stellige Kennnummer, die ein Wertpapier in Deutschland eindeutig identifiziert." },
      { q: "Was ist der Unterschied zwischen WKN und ISIN?", a: "Die WKN ist nur in Deutschland gebräuchlich (6-stellig); die ISIN ist international standardisiert (12-stellig) und enthält die WKN." },
      { q: "Wo finde ich die WKN meines ETFs?", a: "Auf dem Factsheet, im Depot Deiner Bank, bei Finanzportalen wie justETF, extraETF oder onvista." },
    ],
  },

  zinswende: {
    blogLinks: [{ path: "/blog/zinswende-altersvorsorge-2024", label: "Zinswende und Altersvorsorge" }],
    faqs: [
      { q: "Was ist die Zinswende?", a: "Der Übergang von einer Niedrigzinsphase zu deutlich höheren Marktzinsen." },
      { q: "Wie profitiere ich davon?", a: "Bessere Tagesgeld- und Festgeldzinsen nutzen, ggf. Geldmarkt-ETFs prüfen, Anleihen wieder ins Portfolio aufnehmen." },
      { q: "Was bedeutet sie für meine Altersvorsorge?", a: "Klassische Garantieprodukte werden wieder etwas attraktiver, aber breit gestreute Aktien-ETFs bleiben für den langfristigen Vermögensaufbau erste Wahl." },
    ],
  },

  zugewinngemeinschaft: {
    blogLinks: [{ path: "/blog/vermoegen-vererben-schenkung-erbschaft", label: "Vermögen vererben – Schenkung und Erbschaft steuerlich klug gestalten" }],
    faqs: [
      { q: "Was ist die Zugewinngemeinschaft?", a: "Der gesetzliche Güterstand der Ehe in Deutschland – Vermögen bleibt getrennt, Zugewinn wird bei Beendigung der Ehe ausgeglichen." },
      { q: "Was passiert bei Scheidung?", a: "Der während der Ehe erzielte Zugewinn wird ausgeglichen: Wer mehr dazugewonnen hat, zahlt die Hälfte der Differenz an den Partner." },
      { q: "Welche Alternativen gibt es?", a: "Gütertrennung (komplett getrennt) oder Gütergemeinschaft (alles gemeinsam) – jeweils per notariellem Ehevertrag." },
    ],
  },

  zollfreigrenze: {
    blogLinks: [
      { path: "/blog/zollfreigrenze-150-euro-abgeschafft-2026", label: "Was das Ende der Zollfreigrenze für Dich bedeutet" },
    ],
    faqs: [
      { q: "Was war die Zollfreigrenze?", a: "Die Zollfreigrenze war eine EU-Regelung die Pakete aus Drittstaaten bis zu einem Warenwert von 150 Euro von Zollgebühren befreit hat. Sie galt bis zum 30. Juni 2026." },
      { q: "Warum wurde die Zollfreigrenze abgeschafft?", a: "Die EU hat sie abgeschafft um gleiche Wettbewerbsbedingungen für europäische Händler zu schaffen und Betrug bei der Warenwertdeklaration zu reduzieren." },
      { q: "Was kostet eine Bestellung aus China jetzt?", a: "3 Euro Zollpauschale pro Warenkategorie plus 19% Einfuhrumsatzsteuer auf den Warenwert. Bei einem 10-Euro-Artikel sind das insgesamt rund 14,90 Euro." },
      { q: "Gibt es noch Ausnahmen?", a: "Persönliche Geschenke zwischen Privatpersonen können weiterhin bis 45 Euro zollfrei sein. Für Plattformkäufe gilt die neue Pauschale ohne Ausnahme." },
      { q: "Wie wirkt sich die neue Regelung auf mein Budget aus?", a: "Billigkäufe bei Temu & Co. werden spürbar teurer. Wer das eingesparte Geld stattdessen investiert, kann langfristig Vermögen aufbauen." },
    ],
  },

  einfuhrumsatzsteuer: {
    blogLinks: [
      { path: "/blog/zollfreigrenze-150-euro-abgeschafft-2026", label: "Was die neue Zollregelung für Dich bedeutet" },
    ],
    faqs: [
      { q: "Was ist die Einfuhrumsatzsteuer?", a: "Die Einfuhrumsatzsteuer ist eine Steuer von 19 Prozent auf den Warenwert bei Importen aus Ländern außerhalb der EU — vergleichbar mit der deutschen Mehrwertsteuer." },
      { q: "Wann fällt die Einfuhrumsatzsteuer an?", a: "Bei jeder Einfuhr von Waren aus Drittstaaten außerhalb der EU — unabhängig vom Warenwert seit dem 1. Juli 2026." },
      { q: "Wie hoch ist die Einfuhrumsatzsteuer?", a: "In Deutschland 19 Prozent des Warenwertes. Bei bestimmten Waren (z.B. Bücher, Lebensmittel) gilt der ermäßigte Satz von 7 Prozent." },
      { q: "Ist die Einfuhrumsatzsteuer dasselbe wie Zoll?", a: "Nein. Zoll und Einfuhrumsatzsteuer sind zwei separate Abgaben die beide anfallen können. Der Zoll richtet sich nach der Warenart, die Einfuhrumsatzsteuer nach dem Warenwert." },
      { q: "Kann ich die Einfuhrumsatzsteuer als Verbraucher von der Steuer absetzen?", a: "Nein. Als Privatperson ist die Einfuhrumsatzsteuer nicht absetzbar. Nur Unternehmen können sie als Vorsteuer geltend machen." },
    ],
  },

  "erste-taetigkeitsstaette": {
    blogLinks: [
      { path: "/blog/erste-taetigkeitsstaette-jahressteuergesetz-2026", label: "Erste Tätigkeitsstätte 2027 – was sich für Arbeitnehmer ändert" },
    ],
    faqs: [
      { q: "Was ist die erste Tätigkeitsstätte?", a: "Die erste Tätigkeitsstätte ist eine ortsfeste betriebliche Einrichtung des Arbeitgebers, eines verbundenen Unternehmens oder eines Kunden, der ein Arbeitnehmer dauerhaft zugeordnet ist. Sie bestimmt ob Fahrtkosten als Pendlerpauschale oder als Dienstreise abgerechnet werden können." },
      { q: "Ist mein Homeoffice die erste Tätigkeitsstätte?", a: "Nein. Das häusliche Arbeitszimmer ist keine betriebliche Einrichtung des Arbeitgebers und kann daher nie erste Tätigkeitsstätte sein — auch nicht bei überwiegender Homeoffice-Nutzung." },
      { q: "Was ändert sich 2027?", a: "Die Frist für eine dauerhafte Zuordnung soll von 48 auf 24 Monate verkürzt werden. Damit gilt ein Projektstandort schon nach zwei Jahren als erste Tätigkeitsstätte." },
      { q: "Was ist der Unterschied zur Pendlerpauschale?", a: "Die Pendlerpauschale (seit 2026 einheitlich 0,38 Euro ab dem ersten Kilometer) gilt nur für Fahrten zur ersten Tätigkeitsstätte — einfache Strecke, keine Verpflegung. Ohne erste Tätigkeitsstätte sind Fahrten als Dienstreise mit vollem Kilometersatz und Verpflegungsmehraufwand absetzbar." },
      { q: "Wie bestimme ich meine erste Tätigkeitsstätte?", a: "Maßgeblich ist die arbeitsrechtliche Zuordnung durch den Arbeitgeber. Fehlt eine eindeutige Zuordnung, gelten quantitative Kriterien (z.B. arbeitstäglich, zwei volle Arbeitstage pro Woche oder ein Drittel der Arbeitszeit an einem Ort)." },
    ],
  },

  verpflegungsmehraufwand: {
    blogLinks: [
      { path: "/blog/erste-taetigkeitsstaette-jahressteuergesetz-2026", label: "Erste Tätigkeitsstätte 2027 – was sich für Arbeitnehmer ändert" },
    ],
    faqs: [
      { q: "Was ist der Verpflegungsmehraufwand?", a: "Der Verpflegungsmehraufwand (VMA) ist eine steuerliche Pauschale für die höheren Essens- und Getränkekosten, die bei Dienstreisen außerhalb der ersten Tätigkeitsstätte anfallen." },
      { q: "Wie hoch ist der VMA 2026?", a: "14 Euro bei einer Abwesenheit von mehr als 8 Stunden, 28 Euro bei ganztägiger Abwesenheit (24 Stunden). An An- und Abreisetagen mehrtägiger Reisen jeweils 14 Euro ohne Zeitgrenze." },
      { q: "Wann darf ich VMA geltend machen?", a: "Nur bei beruflich veranlassten Auswärtstätigkeiten außerhalb der ersten Tätigkeitsstätte und der Wohnung. Voraussetzung ist, dass am Einsatzort keine erste Tätigkeitsstätte begründet wird." },
      { q: "Was passiert wenn eine erste Tätigkeitsstätte entsteht?", a: "Sobald ein Einsatzort steuerlich zur ersten Tätigkeitsstätte wird — z.B. ab 2027 nach 24 Monaten am selben Projektstandort — entfällt der Anspruch auf Verpflegungsmehraufwand vollständig." },
      { q: "Kann mein Arbeitgeber den VMA steuerfrei erstatten?", a: "Ja. Der Arbeitgeber kann die gesetzlichen Pauschalen (14 bzw. 28 Euro) steuer- und sozialversicherungsfrei erstatten. Höhere Erstattungen sind bis zum doppelten Satz möglich, dann aber pauschal mit 25 Prozent zu versteuern." },
    ],
  },

  energiesteuer: {
    blogLinks: [
      { path: "/blog/tankrabatt-weg-2026-fahrtkosten-steuern", label: "Tankrabatt weg — so holst Du Dir das Geld über die Steuererklärung zurück" },
    ],
    faqs: [
      { q: "Was ist die Energiesteuer?", a: "Die Energiesteuer (früher Mineralölsteuer) ist eine Verbrauchsteuer auf Energieträger wie Benzin, Diesel, Flüssiggas und Erdgas. Sie wird vom Bund erhoben und ist im Energiesteuergesetz geregelt." },
      { q: "Wie hoch ist die Energiesteuer auf Benzin 2026?", a: "Der reguläre Steuersatz beträgt 65,45 Cent pro Liter Benzin und 47,04 Cent pro Liter Diesel. Seit dem 1. Juli 2026 gilt nach dem Auslaufen des Tankrabatts wieder der volle Satz." },
      { q: "Was ist der Unterschied zwischen Energiesteuer und Mehrwertsteuer?", a: "Die Energiesteuer ist eine Verbrauchsteuer die pro Liter Kraftstoff erhoben wird. Die Mehrwertsteuer (19%) wird zusätzlich auf den gesamten Bruttopreis inklusive Energiesteuer berechnet — man zahlt also Steuer auf die Steuer." },
      { q: "Was ist ein Tankrabatt?", a: "Ein Tankrabatt ist eine temporäre Absenkung der Energiesteuer zur Entlastung der Verbraucher. Der letzte Tankrabatt in Deutschland ist zum 30. Juni 2026 ausgelaufen." },
      { q: "Wie beeinflusst die Energiesteuer den Spritpreis?", a: "Die Energiesteuer macht zusammen mit der Mehrwertsteuer über 50 Prozent des Spritpreises aus. Änderungen der Energiesteuer wirken sich daher direkt und spürbar auf die Preise an der Zapfsäule aus." },
    ],
  },

  pflegeluecke: {
    blogLinks: [
      { path: "/blog/pflegemindestlohn-2026-kosten-absicherung", label: "Pflegemindestlohn 2026 — mehr Lohn, höhere Eigenanteile" },
    ],
    faqs: [
      { q: "Was ist die Pflegelücke?", a: "Die Pflegelücke ist die Differenz zwischen den Leistungen der gesetzlichen Pflegeversicherung und den tatsächlichen Kosten für professionelle Pflege — den Rest müssen Pflegebedürftige und ihre Familien selbst tragen." },
      { q: "Wie hoch ist die Pflegelücke 2026?", a: "Sie beträgt heute oft 2.000 bis 3.000 Euro monatlich. Durch den steigenden Pflegemindestlohn ab Juli 2026 wächst sie voraussichtlich um weitere 50 bis 100 Euro pro Monat." },
      { q: "Wie schließe ich die Pflegelücke?", a: "Es gibt zwei Wege: eine private Pflegezusatzversicherung die die Lücke absichert, oder frühzeitiger Vermögensaufbau z.B. über einen ETF-Sparplan. Am sinnvollsten ist eine Kombination aus beiden." },
      { q: "Was leistet die gesetzliche Pflegeversicherung?", a: "Sie zahlt je nach Pflegegrad einen festen monatlichen Zuschuss zu ambulanter oder stationärer Pflege. Da diese Beträge fixiert sind und Pflegekosten schneller steigen als die Leistungen, wächst die Pflegelücke jährlich." },
      { q: "Wann sollte ich Pflegevorsorge abschließen?", a: "So früh wie möglich. Je jünger und gesunder Du beim Abschluss einer Pflegezusatzversicherung bist, desto günstiger der Beitrag. Auch der Vermögensaufbau profitiert vom Zinseszinseffekt." },
    ],
  },

  pflegemindestlohn: {
    blogLinks: [
      { path: "/blog/pflegemindestlohn-2026-kosten-absicherung", label: "Pflegemindestlohn 2026 — mehr Lohn, höhere Eigenanteile" },
    ],
    faqs: [
      { q: "Was ist der Pflegemindestlohn?", a: "Der Pflegemindestlohn ist ein branchenspezifischer gesetzlicher Mindestlohn für Beschäftigte in der Pflege. Er wird auf Grundlage des Arbeitnehmer-Entsendegesetzes festgesetzt und liegt deutlich über dem allgemeinen Mindestlohn." },
      { q: "Wie hoch ist der Pflegemindestlohn 2026?", a: "Ab dem 1. Juli 2026 beträgt er 16,52 Euro für Pflegehilfskräfte, 17,80 Euro für qualifizierte Pflegehilfskräfte und 21,03 Euro für Pflegefachkräfte mit mindestens dreijähriger Ausbildung." },
      { q: "Für wen gilt er?", a: "Für rund 1,3 Millionen Beschäftigte in ambulanten und stationären Pflegeeinrichtungen — verbindlich für alle Arbeitgeber, unabhängig von tarifvertraglichen Vereinbarungen. In Privathaushalten gilt der allgemeine Mindestlohn." },
      { q: "Was ist der Unterschied zum allgemeinen Mindestlohn?", a: "Der allgemeine Mindestlohn beträgt 2026 13,90 Euro pro Stunde. Der Pflegemindestlohn liegt je nach Qualifikation deutlich darüber und wird von einer eigenen Kommission festgelegt." },
      { q: "Wann wird er erhöht?", a: "Die nächste Erhöhung erfolgt zum 1. Juli 2027 — dann steigen die Sätze auf 16,95 Euro, 18,26 Euro und 21,58 Euro pro Stunde." },
    ],
  },

  "rente-mit-63": {
    blogLinks: [
      { path: "/blog/rentenkommission-2026-empfehlungen-altersvorsorge", label: "Rentenkommission 2026 — was die 33 Empfehlungen für Deine Altersvorsorge bedeuten" },
    ],
    faqs: [
      { q: "Was ist die Rente mit 63?", a: "Die Rente mit 63 (offiziell: Altersrente für besonders langjährig Versicherte) ermöglicht Arbeitnehmern mit mindestens 45 Beitragsjahren ohne Abschläge vor dem gesetzlichen Regelrentenalter in Rente zu gehen. Sie wurde 2014 eingeführt." },
      { q: "Wer hat Anspruch auf Rente mit 63?", a: "Anspruch hat, wer mindestens 45 Beitragsjahre in der gesetzlichen Rentenversicherung nachweisen kann. Anerkannt werden neben Pflichtbeiträgen aus Beschäftigung auch Zeiten der Kindererziehung und teilweise Zeiten des Arbeitslosengeld-Bezugs." },
      { q: "Wird die Rente mit 63 abgeschafft?", a: "Die Rentenkommission empfiehlt 2026 die Abschaffung. Eine politische Entscheidung ist noch nicht gefallen — die Empfehlungen zielen auf den Zeitraum nach 2031. Bestehende Ansprüche sind kurzfristig nicht betroffen." },
      { q: "Was sind die Voraussetzungen?", a: "45 Beitragsjahre in der gesetzlichen Rentenversicherung und das Erreichen des maßgeblichen Alters. Für die Jahrgänge ab 1964 gilt: abschlagsfrei erst mit 65 Jahren." },
      { q: "Was passiert wenn ich nicht 45 Beitragsjahre habe?", a: "Dann bleibt die Rente für langjährig Versicherte (35 Beitragsjahre ab 63) mit Abschlägen von 0,3 Prozent pro vorgezogenem Monat — maximal 14,4 Prozent. Die Rentenkommission empfiehlt hier eine Anhebung des Zugangsalters auf 64 Jahre." },
    ],
  },

  rentenkommission: {
    blogLinks: [
      { path: "/blog/rentenkommission-2026-empfehlungen-altersvorsorge", label: "Rentenkommission 2026 — was die 33 Empfehlungen für Deine Altersvorsorge bedeuten" },
    ],
    faqs: [
      { q: "Was ist die Rentenkommission?", a: "Die Rentenkommission ist ein von der Bundesregierung eingesetztes unabhängiges Expertengremium mit dem Auftrag, Empfehlungen für die langfristige Stabilität des gesetzlichen Rentensystems in Deutschland zu erarbeiten." },
      { q: "Wer sitzt in der Rentenkommission?", a: "Wissenschaftler, Vertreter der Sozialpartner (Arbeitgeber und Gewerkschaften) sowie weitere unabhängige Experten aus Rentenpolitik, Wirtschaft und Sozialforschung." },
      { q: "Sind die Empfehlungen bindend?", a: "Nein. Die Empfehlungen der Rentenkommission sind nicht rechtlich bindend. Sie geben aber die politische Richtung vor und dienen als Grundlage für Reformgesetze." },
      { q: "Was hat die Rentenkommission 2026 empfohlen?", a: "Insgesamt 33 Empfehlungen für die Zeit nach 2031 — darunter die Abschaffung der Rente mit 63, die Anhebung des frühestmöglichen Frührenten-Alters von 63 auf 64 Jahre und die langfristige Stabilisierung der Rentenversicherungsbeiträge." },
      { q: "Wann werden die Empfehlungen umgesetzt?", a: "Die Empfehlungen zielen auf den Zeitraum nach 2031. Ob und in welchem Umfang die Politik sie umsetzt, ist noch offen und Gegenstand des politischen Prozesses." },
    ],
  },
  "kapitalertragsteuer": {
    blogLinks: [
      { path: "/blog/freistellungsauftrag-richtig-nutzen", label: "Freistellungsauftrag richtig nutzen" },
    ],
  },

  "erwerbsminderungsrente": {
    blogLinks: [
      { path: "/blog/erwerbsminderungsrente-voll-teilweise-2026", label: "Erwerbsminderungsrente 2026: volle oder teilweise — und wie viel Geld kommt an?" },
      { path: "/blog/berufsunfaehigkeitsversicherung-sinnvoll", label: "Berufsunfähigkeitsversicherung – wann sie sinnvoll ist" },
      { path: "/blog/arbeitskraft-absichern-versicherungen", label: "Arbeitskraft absichern – welche Versicherungen wirklich zählen" },
    ],
    faqs: [
      { q: "Wann bekomme ich die volle Erwerbsminderungsrente?", a: "Wenn Du auf dem allgemeinen Arbeitsmarkt weniger als 3 Stunden täglich arbeiten kannst (§ 43 SGB VI). Bei 3 bis unter 6 Stunden gibt es nur die teilweise Erwerbsminderungsrente in halber Höhe." },
      { q: "Wie hoch ist die Erwerbsminderungsrente im Durchschnitt?", a: "Der durchschnittliche Zahlbetrag lag im Rentenzugang 2024 bei 1.041 € monatlich (Männer 1.078 €, Frauen 1.010 €) – Statistik der Deutschen Rentenversicherung." },
      { q: "Welche Voraussetzungen muss ich erfüllen?", a: "Die allgemeine Wartezeit von 5 Jahren und mindestens 36 Monate Pflichtbeiträge in den letzten 60 Monaten vor Eintritt der Erwerbsminderung." },
      { q: "Wie viel darf ich dazuverdienen?", a: "2026 mindestens 20.763,75 € im Jahr bei voller und mindestens 41.527,50 € bei teilweiser Erwerbsminderungsrente. Bei der teilweisen Erwerbsminderungsrente ist das nur die Mindestgrenze – je nach früherem Einkommen kann die individuelle Grenze höher liegen. Über der Grenze werden 40 Prozent des übersteigenden Betrags angerechnet." },
      { q: "Ersetzt die Erwerbsminderungsrente eine Berufsunfähigkeitsversicherung?", a: "Nein. Die gesetzliche Rente kennt keinen Berufsschutz und fragt nur, ob Du irgendeine Tätigkeit noch 6 Stunden täglich ausüben kannst." },
    ],
  },

  "zurechnungszeit": {
    blogLinks: [
      { path: "/blog/erwerbsminderungsrente-voll-teilweise-2026", label: "Erwerbsminderungsrente 2026: volle oder teilweise — und wie viel Geld kommt an?" },
    ],
    faqs: [
      { q: "Was ist die Zurechnungszeit?", a: "Eine fiktive Beitragszeit nach § 59 SGB VI: Bei Erwerbsminderung wirst Du so gestellt, als hättest Du bis zum gesetzlich festgelegten Endalter mit Deinem bisherigen Durchschnittsverdienst weitergearbeitet. Bei Rentenbeginn 2026 endet die Zurechnungszeit mit 66 Jahren und 3 Monaten (Übergangsregelung § 253a SGB VI)." },
      { q: "Bis zu welchem Alter läuft die Zurechnungszeit 2026?", a: "Bei einem Rentenbeginn im Jahr 2026 endet die Zurechnungszeit mit 66 Jahren und 3 Monaten. Das Endalter wird seit 2019 schrittweise angehoben (§ 253a SGB VI) und erreicht erst bei Rentenbeginn ab 2031 die vollen 67 Jahre." },
      { q: "Gilt die Zurechnungszeit auch für Hinterbliebenenrenten?", a: "Ja, auch bei Witwen-, Witwer- und Waisenrenten wird eine Zurechnungszeit berücksichtigt." },
      { q: "Wie stark erhöht die Zurechnungszeit meine Rente?", a: "Das hängt vom Alter bei Rentenbeginn ab: Je jünger Du bist, desto mehr fiktive Jahre kommen hinzu und desto größer ist der Effekt auf die Entgeltpunkte." },
    ],
  },

  "hinzuverdienstgrenze": {
    blogLinks: [
      { path: "/blog/erwerbsminderungsrente-voll-teilweise-2026", label: "Erwerbsminderungsrente 2026: volle oder teilweise — und wie viel Geld kommt an?" },
    ],
    faqs: [
      { q: "Wie hoch ist die Hinzuverdienstgrenze 2026?", a: "Bei voller Erwerbsminderungsrente mindestens 20.763,75 € im Jahr, bei teilweiser Erwerbsminderungsrente mindestens 41.527,50 €. Der Wert bei der teilweisen Erwerbsminderungsrente ist eine Mindestgrenze; die individuelle Grenze kann je nach früherem Einkommen höher ausfallen." },
      { q: "Was passiert, wenn ich die Grenze überschreite?", a: "40 Prozent des übersteigenden Betrags werden auf die Rente angerechnet. Die Rente entfällt nicht automatisch komplett." },
      { q: "Gibt es bei der Altersrente eine Hinzuverdienstgrenze?", a: "Seit 2023 nicht mehr. Wer die Regelaltersgrenze erreicht hat oder eine vorgezogene Altersrente bezieht, darf unbegrenzt hinzuverdienen." },
      { q: "Zählt bei der Erwerbsminderungsrente nur das Geld?", a: "Nein. Auch die Arbeitszeit ist entscheidend: Wer als voll Erwerbsgeminderter regelmäßig mehr als 3 Stunden täglich arbeitet, riskiert die Rente unabhängig vom Verdienst." },
    ],
  },

  "fruehstart-rente": {
    blogLinks: [
      { path: "/blog/fruehstart-rente-2027-kinder-altersvorsorge", label: "Frühstart-Rente: 10 Euro im Monat vom Staat für Dein Kind" },
    ],
    faqs: [
      { q: "Wie viel zahlt der Staat bei der Frühstart-Rente?", a: "Laut Regierungsentwurf 10 Euro pro Monat vom 6. bis zum 18. Geburtstag, insgesamt also bis zu 1.440 Euro pro Kind. Das Gesetz ist Stand September 2026 noch nicht beschlossen." },
      { q: "Ab wann gibt es die Frühstart-Rente?", a: "Geplant ist der Start am 1. Januar 2027 mit den Geburtsjahrgängen 2020 und 2021; der Jahrgang 2020 erhält die Förderung rückwirkend ab 2026." },
      { q: "Müssen Eltern ein Depot eröffnen?", a: "Ja, um Anbieter und Anlage selbst zu wählen und zuzahlen zu können. Ohne eigenes Depot legt die Deutsche Bundesbank das Geld kollektiv an; die Förderung geht also nicht verloren." },
      { q: "Wann kann das Kind über das Geld verfügen?", a: "Die staatliche Förderung und ihre Erträge werden grundsätzlich nicht vor Vollendung des 65. Lebensjahres ausgezahlt." },
    ],
  },

  standarddepot: {
    blogLinks: [
      { path: "/blog/altersvorsorgedepot-2027-riester-nachfolger", label: "Altersvorsorgedepot – der Riester-Nachfolger" },
      { path: "/blog/fruehstart-rente-2027-kinder-altersvorsorge", label: "Frühstart-Rente: 10 Euro im Monat vom Staat für Dein Kind" },
    ],
    faqs: [
      { q: "Was kostet ein Standarddepot?", a: "Die Effektivkosten des Standarddepots sind in der Ansparphase gesetzlich auf 1 Prozent pro Jahr gedeckelt." },
      { q: "Ab wann kann man ein Standarddepot abschließen?", a: "Standarddepot-Verträge können frühestens ab dem 1. Januar 2027 abgeschlossen werden." },
      { q: "Muss ich im Standarddepot selbst Fonds auswählen?", a: "Nein. Der Anbieter legt vorab zwei Fonds fest — einen Sicherheits- und einen Renditebaustein —, sodass in der Ansparphase keine eigenen Anlageentscheidungen nötig sind." },
    ],
  },

};
